// client/src/pages/instructors/InstructorCourseManagement.jsx

import React, { useState, useEffect, useContext } from 'react';
import { ContentListModal, LessonModal, AssessmentModal,  
          AnnouncementDetailModal, ConversationModal  } from '../../modals';
import { useParams, useNavigate } from 'react-router-dom';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import BackButton from '../../components/customs/backButton';
import { GlobalContext } from '../../context/provider';
import { CourseDomains } from '../../types/courseDomains';
import useInstructor from '../../hooks/useInstructor';
import useCourse from '../../hooks/useCourse';
import useCategory from '../../hooks/useCategory';
import useStudent from '../../hooks/useStudent';
import useBlobStorage from '../../hooks/useBlobStorage';
import useMessage from '../../hooks/useMessage';
import useAnnouncement from '../../hooks/useAnnouncement';
import useSimulator from '../../hooks/useSimulator';
import useVisualCircuit from '../../hooks/useVisualCircuit';
import { 
    formatDate, 
    formatShortDate, 
    formatLongDate, 
    formatRelativeTime,
    formatDateTime } from '../../utilities/dateUtils';
import MessageAnnouncementNotificationManager, { 
  clearMessageNotifications,
} from '../../utilities/messaging/messageAnnouncementNotificationManager';

import '../../styles/globalstyles.css';
import '../../modals/admin/AdminCourseDetailModal.css';
import './instructorCourseManagement.css';

const InstructorCourseManagement = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { 
    authState: { authData },
    courseState: {courseData},  
  } = useContext(GlobalContext);
  
  const { 
    getCourseStudents,
    getCourseLabs,
    getCourseQuizzes,
    getCourseExams,
    getCourseCapstones,
    gradeLab,
    gradeQuiz,
    gradeExam,
    gradeCapstone,
    generateQuestionRubric,
    generateCompleteLabRequirements,
  } = useInstructor();

  const { 
    getCourseById, 
    updateCourseContent, 
    removeLesson, 
    removeLab, 
    removeQuiz, 
    removeExam,
    removeCapstone,
    addLesson,   
    addLab,
    addQuiz,
    addExam,
    addCapstone
  } = useCourse();

  const {
    getCategoryByTitle,
  } = useCategory();

  const { 
    getQuizProgress,
    getExamProgress,
    getLabProgress,
    getCapstoneProgress
  } = useStudent();

  const { 
    getFileDownloadUrl, 
    getFileViewUrl,
    triggerDownload,
    uploadFile
  } = useBlobStorage();

  const { 
    getMessagesAndMarkSeen,
    sendMessage,
    getUnreadCount,
    getUsersWithUnreadCounts,
    messages,
    unreadCount,
    usersWithUnreadCounts
  } = useMessage();

  // Load announcement history when component mounts
  const { 
    sendAnnouncement,
    getAnnouncementHistory,
    getUnreadAnnouncementCount
  } = useAnnouncement();

  const { getSimulatorBySessionId } = useSimulator();
  
  const { courseCircuits, getCircuitsByCourse } = useVisualCircuit();
  
  const [activeTab, setActiveTab] = useState('content');
  const [isLoading, setIsLoading] = useState(true);
  const [courseInfo, setCourseInfo] = useState(null);
  const [tags, setTags] = useState([]);
  const [courseProgress, setCourseProgress] = useState(null);
  const [students, setStudents] = useState([]);
  const [expandedStudents, setExpandedStudents] = useState({});
  
  // Grading data
  const [labSubmissions, setLabSubmissions] = useState([]);
  const [quizSubmissions, setQuizSubmissions] = useState([]);
  const [examSubmissions, setExamSubmissions] = useState([]);
  const [capstoneSubmissions, setCapstoneSubmissions] = useState([]);
  const [labGradingModals, setLabGradingModals] = useState({});

  // AI Rubric Generation States
  const [generatingRubric, setGeneratingRubric] = useState(false);
  const [rubricPreview, setRubricPreview] = useState(null);
  const [rubricQuestionIndex, setRubricQuestionIndex] = useState(null);
  const [editableRubric, setEditableRubric] = useState(null);
  const [isEditingRubric, setIsEditingRubric] = useState(false);
  const [rubricErrors, setRubricErrors] = useState([]);
  
  // Expandable sections
  const [expandedLabs, setExpandedLabs] = useState({});
  const [expandedQuizzes, setExpandedQuizzes] = useState({});
  const [expandedExams, setExpandedExams] = useState({});
  const [expandedCapstones, setExpandedCapstones] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    labInfo: true,
    experimental: true,
    postLab: true
  });

  const [gradingCollapsed, setGradingCollapsed] = useState({});
  const toggleGrading = (key) => setGradingCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  // Modal for viewing submission details
  const [showListModal, setShowListModal] = useState(false);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  const [selectedListType, setSelectedListType] = useState(null);
  const [gradeFormData, setGradeFormData] = useState({ grade: '', maxGrade: 100, feedback: '' });

  // Track grading changes for bulk submit
  const [gradingChanges, setGradingChanges] = useState({});
  const [showRubricEditor, setShowRubricEditor] = useState(false);
  const [editedContentFiles, setEditedContentFiles] = useState({});
  const [uploadingContentFiles, setUploadingContentFiles] = useState(false);
  const [contentFileUploadProgress, setContentFileUploadProgress] = useState({});

  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedContent, setEditedContent] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [allStudentCircuits, setAllStudentCircuits] = useState([]);

  // Grading weight editing
  const [editingWeight, setEditingWeight] = useState(null); // {type, id, currentWeight}
  const [tempWeight, setTempWeight] = useState('');

  // existing state declarations
  const [studentsProgress, setStudentsProgress] = useState({});
  const [statsAssignments, setStatsAssignments] = useState({
    labsPending: 0, labsSubmitted: 0,
    quizzesPending: 0, quizzesSubmitted: 0,
    examsPending: 0, examsSubmitted: 0,
    capstonesPending: 0, capstonesSubmitted: 0,
    allPending: 0, allSubmitted: 0,
  });

  // Message states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [studentsWithUnread, setStudentsWithUnread] = useState([]);

  // Annoucement states
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementBody, setAnnouncementBody] = useState('');
  const [announcementPriority, setAnnouncementPriority] = useState('high');
  const [selectedCourse, setSelectedCourse] = useState('all');
  const [announcementHistory, setAnnouncementHistory] = useState([]);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [unreadAnnouncementCount, setUnreadAnnouncementCount] = useState(0);
  
  // course circuit
  const [myCircuitsCount, setMyCircuitsCount] = useState(0);
  const [domainName, setDomainName] = useState('');
  
  // Notification states
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [newAnnouncementsCount, setNewAnnouncementsCount] = useState(0);
  const [messageText, setMessageText] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementModalVisible, setAnnouncementModalVisible] = useState(false);

  
  const [expandedRubrics, setExpandedRubrics] = useState({});

  useEffect(() => {
    loadPage();
  }, [courseId]);

  // Update notification badges when unread counts change
  useEffect(() => {
    if (!authData?.id) return;
    // Count only announcements for THIS course
    const courseAnnouncementCount = announcementHistory?.filter(
      announcement => announcement.courseId === courseId
    ).length || 0;
    
    console.log("📊 Course notification counts:", {
      messages: unreadCount || 0,
      courseAnnouncements: courseAnnouncementCount,
      total: (unreadCount || 0) + courseAnnouncementCount
    });
    
  }, [unreadCount, announcementHistory, courseId, authData?.id]);
  
  const loadPage = async () => {
    setIsLoading(true);

    try {
      const courseResponse = await getCourseById(courseId);
      setDomainName(courseResponse.data.category);
      console.log("Course Info: ", courseResponse);
      if (courseResponse.success) {
        setCourseInfo(courseResponse.data);
        if (courseResponse?.data?.category) {
          const response = await getCategoryByTitle(courseResponse.data.category);
          if (response?.success && response.data?.tags) {
            setTags(response.data.tags);
          }
        }
      }

      if (courseId && authData?.id) {
          const result = await getCircuitsByCourse(courseId, authData.id);
          setMyCircuitsCount(result?.data?.length || 0);
      }

      const studentsResponse = await getCourseStudents(authData.id, courseId);
      const courseStudents = studentsResponse?.data?.students || [];
      if (studentsResponse.success) {
        setStudents(courseStudents);
      }

      // Get all submissions using instructor hooks
      const [labs, quizzes, exams, capstones] = await Promise.all([
        getCourseLabs(authData.id, courseId),
        getCourseQuizzes(authData.id, courseId),
        getCourseExams(authData.id, courseId),
        getCourseCapstones(authData.id, courseId)
      ]);

      // console.log("Quiz submissions received:", quizzes?.data);
      // console.log("Exam submissions received:", exams?.data);

      // console.timeLog("===============Submission=================");
      // console.log("labs submissions: ", labs);
      // console.log("quizzes submissions: ", quizzes);
      // console.log("exams submissions: ", exams);
      // console.log("capstonnes submissions: ", capstones);

      // Extract data arrays first
      const labsData = labs?.data || [];
      const quizzesData = quizzes?.data || [];
      const examsData = exams?.data || [];
      const capstonesData = capstones?.data || [];
  
      // Now filter the arrays
      const labsPending = labsData.filter(s => s.status === 'submitted').length;
      const quizzesPending = quizzesData.filter(s => s.status === 'submitted').length;
      const examsPending = examsData.filter(s => s.status === 'submitted').length;
      const capstonesPending = capstonesData.filter(s => s.status === 'submitted').length;
      const totalPending = labsPending + quizzesPending + examsPending + capstonesPending;

      const labsGraded = labsData.filter(s => s.status === 'graded').length;
      const quizzesGraded = quizzesData.filter(s => s.status === 'graded').length;
      const examsGraded = examsData.filter(s => s.status === 'graded').length;
      const capstonesGraded = capstonesData.filter(s => s.status === 'graded').length;
      const totalGraded = labsGraded + quizzesGraded + examsGraded + capstonesGraded;

      const labsAll = labsGraded + labsPending;
      const examsAll = examsGraded + examsPending;
      const quizzesAll = quizzesGraded + quizzesPending;
      const capstonesAll = capstonesGraded + capstonesPending;
      const totalAll = totalGraded + totalPending;

      setStatsAssignments({
        labsPending: labsPending, labsSubmitted: labsAll,
        quizzesPending: quizzesPending, quizzesSubmitted: quizzesAll,
        examsPending: examsPending, examsSubmitted: examsAll,
        capstonesPending: capstonesPending, capstonesSubmitted: capstonesAll,
        allPending: totalPending, allSubmitted: totalAll 
      })

      setLabSubmissions(labs?.data || []);
      setQuizSubmissions(quizzes?.data || []);
      setExamSubmissions(exams?.data || []);
      setCapstoneSubmissions(capstones?.data || []);

      // Fetch progress data for ALL students using student hooks
      const progressData = {};
      
      // load student progress
      for (const student of courseStudents) {
        const loginId = student.loginId || student.studentId;
        try {
          // Use the 4 separate APIs to get all progress for each student
          const [labProgressRes, quizProgressRes, examProgressRes, capstoneProgressRes] = await Promise.all([
            getLabProgress(loginId, courseId),
            getQuizProgress(loginId, courseId),
            getExamProgress(loginId, courseId),
            getCapstoneProgress(loginId, courseId)
          ]);

          // Combine all progress data into the structure we need
          // Store by loginId to match the submission data
          progressData[loginId] = {
            labProgress: labProgressRes.success ? labProgressRes.data : { courseId, labs: [] },
            quizProgress: quizProgressRes.success ? quizProgressRes.data : { courseId, quizzes: [] },
            examProgress: examProgressRes.success ? examProgressRes.data : { courseId, exams: [] },
            capstoneProjects: capstoneProgressRes.success && capstoneProgressRes.data 
              ? capstoneProgressRes.data 
              : { courseId, capstones: [] },
            totalLabs: labProgressRes.data?.labs?.length || 0,
            totalQuizzes: quizProgressRes.data?.quizzes?.length || 0,
            totalExams: examProgressRes.data?.exams?.length || 0,
            totalCapstones: capstoneProgressRes.data?.capstones?.length || 0
          };
        } catch (error) {
          console.error(`Failed to load progress for student ${loginId}:`, error);
          // Initialize empty progress for this student
          progressData[loginId] = {
            labProgress: { courseId, labs: [] },
            quizProgress: { courseId, quizzes: [] },
            examProgress: { courseId, exams: [] },
            capstoneProjects: { courseId, capstones: [] },
            totalLabs: 0,
            totalQuizzes: 0,
            totalExams: 0,
            totalCapstones: 0
          };
        }
      }

      setStudentsProgress(progressData);
      console.log("Students progress loaded:", progressData);

      const circuitResults = await Promise.all(
          courseStudents.map(s =>
              getCircuitsByCourse(courseId, s.loginId || s.studentId).catch(() => ({ data: [] }))
          )
      );
      // Each result is { success, data: [...], count } — extract the data array
      const flatCircuits = circuitResults.flatMap(res => res?.data || []);
      setAllStudentCircuits(flatCircuits);

      // Load unread messages
      try {
        await getUnreadCount(authData.id);
      } catch (error) {
        console.error('Failed to load unread count:', error);
      }

      // Load unread announcement count for this course
      try {
        const unreadAnnouncementResponse = await getUnreadAnnouncementCount(authData.id);
        if (unreadAnnouncementResponse.success) {
          // We'll filter to only count announcements for THIS course in the useEffect
          setUnreadAnnouncementCount(unreadAnnouncementResponse.unreadCount || 0);
        }
      } catch (error) {
        console.error('Failed to load unread announcement count:', error);
      }
        
      // Load students with unread message counts
      if (courseStudents.length > 0) {
        try {
          await getUsersWithUnreadCounts(authData.id, courseStudents);
          // The hook will update usersWithUnreadCounts state automatically
        } catch (error) {
          console.error('Failed to load students with unread counts:', error);
        }
      }

      // Load announcement history
      try {
        await loadAnnouncementHistory();
      } catch (error) {
        console.error('Failed to load announcements:', error);
      }

    } catch (error) {
      console.error('Failed to load course data:', error);
      showNotification('Failed to load course data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const [hasTextbook, setHasTextbook] = useState(false);

  useEffect(() => {
      if (!courseInfo?.category) {
          setHasTextbook(false);
          return;
      }
      setHasTextbook(false);
      const url = `/courses/${encodeURIComponent(courseInfo.category)}/textbook.json`;
      console.log('Checking textbook at:', url);
      fetch(url)
          .then(res => {
              console.log('Textbook response:', res.ok, res.status, res.headers.get('content-type'));
              const contentType = res.headers.get('content-type');
              setHasTextbook(res.ok && contentType?.includes('application/json'));
          })
          .catch((err) => {
              console.log('Textbook fetch error:', err);
              setHasTextbook(false);
          });
  }, [courseInfo?.category]);

  const specialCasing = {
    iot: 'IoT',
    ai: 'AI',
    mcq: 'MCQ'
  };

  const formatType = (str) => {
    const cleaned = str.replace('_', ' ');
    return cleaned
      .split(' ')
      .map(word => specialCasing[word.toLowerCase()] || word)
      .join(' ');
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    notification.className = 'notification-toast';
    const bgColors = {
      success: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      error: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      info: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
    };
    const icons = { success: '✅', error: '❌', info: 'ℹ️' };
    
    notification.style.background = bgColors[type] || bgColors.info;
    notification.innerHTML = `
      <span class="notification-icon">${icons[type]}</span>
      <span>${message}</span>
    `;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out forwards';
      setTimeout(() => document.body.removeChild(notification), 300);
    }, 3000);
  };

  const toggleExpand = (type, id) => {
    const setters = {
      lab: setExpandedLabs,
      quiz: setExpandedQuizzes,
      exam: setExpandedExams,
      capstone: setExpandedCapstones
    };
    setters[type]?.(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openSubmissionModal = async (submission) => {
    try {
      let fullSubmission = { ...submission };
      const studentId = submission.studentLoginId || submission.loginId;
      
      // Get the student's progress data from state
      const studentProgress = studentsProgress[studentId];
      
      if (!studentProgress) {
        showNotification('Student progress data not found', 'error');
        return;
      }
      
      // Build full submission based on type
      if (submission.type === 'quiz') {
        const quiz = courseInfo?.quizzes?.find(q => q.quizId === submission.quizId);
        const quizProgress = studentProgress.quizProgress.quizzes?.find(q => q.quizId === submission.quizId);
        const attempt = quizProgress?.attempts?.find(a => a.attemptNumber === submission.attemptNumber);
        
        if (quiz && attempt) {
          fullSubmission = {
            ...submission,
            questions: quiz.questions,
            answers: attempt.answers || [],
            maxGrade: quiz.maxGrade,
            status: attempt.status || submission.status
          };
        }
      } else if (submission.type === 'exam') {
        const exam = courseInfo?.exams?.find(e => e.examId === submission.examId);
        const examProgress = studentProgress.examProgress.exams?.find(e => e.examId === submission.examId);
        const attempt = examProgress?.attempts?.find(a => a.attemptNumber === submission.attemptNumber);
        
        if (exam && attempt) {
          fullSubmission = {
            ...submission,
            questions: exam.questions,
            answers: attempt.answers || [],
            maxGrade: exam.maxGrade,
            status: attempt.status || submission.status
          };
        }
      } else if (submission.type === 'lab') {
        const lab = courseInfo?.labs?.find(e => e.labId === submission.labId);
        const labProgress = studentProgress.labProgress.labs?.find(e => e.labId === submission.labId);
        const attempt = labProgress?.attempts?.find(a => a.attemptNumber === submission.attemptNumber);
        
        if (lab) {
          fullSubmission = {
            ...submission,
            questions: lab.questions,
            answers: attempt?.answers || [],
            maxGrade: lab.maxGrade,
            status: attempt?.status || submission.status || 'not_started'
          };
          
          console.log('🔌 Circuits for student:', allStudentCircuits?.filter(c => c.labId === submission.labId));

          // Check for Lab practical questions and fetch session data
          for (const question of lab.questions) {
            if (question.questionType === 'lab_practical') {
              // First try from attempt answers
              const answer = attempt?.answers?.find(a => a.questionId === question.questionId);
              const sessionId = answer?.sessionId ||
                allStudentCircuits?.find(c =>
                  c.courseId === courseId &&
                  c.labId === submission.labId &&
                  c.questionId === question.questionId &&
                  (c.studentId === studentId || c.loginId === studentId) &&
                  c.sessionId
                )?.sessionId;

              if (sessionId) {
                try {
                  const simResponse = await getSimulatorBySessionId(sessionId);
                  if (simResponse.success && simResponse.data) {
                    if (!fullSubmission.labSessions) {
                      fullSubmission.labSessions = {};
                    }
                    fullSubmission.labSessions[question.questionId] = simResponse.data;
                  }
                  // If 404 or no data — skip silently, no button will show
                } catch (error) {
                  console.error(`Simulator not found for question ${question.questionId} — skipping`);
                }
              }
            }
          }        
        }
      } else if (submission.type === 'capstone') {
        const capstone = courseInfo?.capstones?.find(c => c.capstoneId === submission.capstoneId);
        
        if (capstone) {
          fullSubmission = {
            ...submission,
            maxGrade: capstone.maxGrade
          };
        }
      }
      
      setSelectedSubmission(fullSubmission);
      console.log("full Submission data:", fullSubmission);
      
      // Initialize grade form with per-question grading structure
      const questionGrades = {};
      if (fullSubmission.answers) {
        fullSubmission.answers.forEach(answer => {
          questionGrades[answer.questionId] = {
            points: answer.points || 0,
            feedback: answer.feedback || ''
          };
        });
      }
      
      setGradeFormData({
        grade: submission.grade || '',
        maxGrade: submission.maxGrade || fullSubmission.maxGrade || 100,
        feedback: submission.feedback || '',
        questionGrades: questionGrades
      });
      
      setShowSubmissionModal(true);
    } catch (error) {
      console.error('Error loading submission details:', error);
      showNotification('Failed to load submission details', 'error');
    }
  };

  const closeSubmissionModal = () => {
    setShowSubmissionModal(false);
    setSelectedSubmission(null);
    setGradeFormData({ grade: '', maxGrade: 100, feedback: '' });
  };

  // Handle selecting a student for messaging
  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);

    // ✅ ADD: Clear notification for this specific conversation
    clearMessageNotifications(authData.id);
    setNewMessagesCount(0);
    
    // Load conversation with this student
    try {
      const response = await getMessagesAndMarkSeen(
        student.loginId,
        authData.id,
        { page: 1, limit: 50 }
      );
      
      if (response.success) {
        setConversationMessages(response.messages || []);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
      showNotification('Failed to load messages', 'error');
    }
  };

  // Handle sending a message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() || !selectedStudent) {
      showNotification('Please enter a message', 'error');
      return;
    }
    
    try {
      const messageData = {
        targetUserId: selectedStudent.loginId,
        targetName: selectedStudent.name,
        title: `Message from ${authData.name}`,
        body: messageText,
        senderUserId: authData.id,
        senderName: authData.name,
        type: 'general',
        priority: 'medium',
        actionRequired: false
      };
      
      const response = await sendMessage(messageData);
      
      if (response.success) {
        showNotification('Message sent successfully', 'success');
        setMessageText('');
        
        // Reload conversation
        handleSelectStudent(selectedStudent);
      } else {
        showNotification('Failed to send message', 'error');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      showNotification('An error occurred', 'error');
    }
  };

  // Load announcement history
  const loadAnnouncementHistory = async () => {
    try {
      const response = await getAnnouncementHistory(authData.id);
      if (response.success) {
        // ✅ Filter announcements for ONLY this specific course
        const courseAnnouncements = (response.announcements || []).filter(
          announcement => announcement.courseId === courseId
        );
        setAnnouncementHistory(courseAnnouncements);
      }
    } catch (error) {
      console.error('Failed to load announcement history:', error);
    }
  };

  const handleSendCourseAnnouncement = async () => {
    if (!announcementTitle || !announcementBody) {
      showNotification("Fill in all fields", "error"); return;
    }
    setSendingAnnouncement(true);
    try {
      const res = await sendAnnouncement({
        courseId: courseId,
        title: announcementTitle,
        body: announcementBody,
        priority: announcementPriority,
        instructorId: authData.id,
      });
      if (res.success) {
        showNotification("Announcement sent", "success");
        setAnnouncementTitle(""); setAnnouncementBody("");
        // reload history
      }
    } finally { setSendingAnnouncement(false); }
  };

  const handleInlineGradeChange = (submissionKey, field, value) => {
    setGradingChanges(prev => ({
      ...prev,
      [submissionKey]: {
        ...prev[submissionKey],
        [field]: value
      }
    }));
  };

  const handleSubmitSingleGrade = async () => {
    try {
      const submission = selectedSubmission;
      let response;
      if (submission.type === 'lab') {
        // Per-question grading for labs
        const gradedQuestions = gradeFormData.questionGrades 
          ? Object.entries(gradeFormData.questionGrades).map(([questionId, grading]) => {
            // Find the student's answer to check for AI grading
            const override = gradeFormData.answers?.[questionId];
            const points = override?.points !== undefined ? override.points : grading.points;
            const feedback = override?.feedback !== undefined ? override.feedback : grading.feedback;
            const studentAnswer = submission.answers.find(a => a.questionId === questionId);
            const aiMetadata = studentAnswer?.aiGradeMetadata;
            const hasAIGrade = !!aiMetadata?.aiScore;
            const isOverriding = hasAIGrade && parseFloat(points) !== aiMetadata.aiScore;

            const questionGrade = {
              questionId,
              pointsAwarded: parseFloat(points) || 0,
              feedback: feedback || ''
            };
            
            // Add override metadata if instructor is changing AI grade
            if (isOverriding) {
              questionGrade.aiGradeMetadata = {
                ...aiMetadata,
                overriddenByInstructor: true,
                overriddenBy: authData.id,
                overriddenAt: new Date().toISOString(),
                overrideReason: grading.overrideReason || 'Instructor adjusted grade',
                originalAIScore: aiMetadata.aiScore,
                originalAIFeedback: aiMetadata.aiFeedback
              };
            }
            
            return questionGrade;
          })
          : [];
        
        console.log("Grading data being sent:", {
          studentLoginId: submission.loginId,
          courseId: courseId,
          labId: submission.labId,
          attemptNumber: submission.attemptNumber,
          gradedQuestions: gradedQuestions
        });
        // ✅ FIX: Calculate total grade from all graded questions
        const totalGrade = gradedQuestions.reduce((sum, q) => sum + q.pointsAwarded, 0);
        
        const requestPayload = {
          studentLoginId: submission.loginId,
          courseId: courseId,
          labId: submission.labId,
          attemptNumber: submission.attemptNumber,  // ✅ Make sure this exists
          gradedQuestions: gradedQuestions,
          overallFeedback: gradeFormData.feedback,
          grade: totalGrade,
          maxGrade: gradeFormData.maxGrade
        };

        response = await gradeLab(authData.id, requestPayload);

      } else if (submission.type === 'quiz') {
        const gradedQuestions = gradeFormData.questionGrades 
          ? Object.entries(gradeFormData.questionGrades).map(([questionId, grading]) => {
            // Find the student's answer to check for AI grading
            const override = gradeFormData.answers?.[questionId];
            const points = override?.points !== undefined ? override.points : grading.points;
            const feedback = override?.feedback !== undefined ? override.feedback : grading.feedback;
            const studentAnswer = submission.answers.find(a => a.questionId === questionId);
            const aiMetadata = studentAnswer?.aiGradeMetadata;
            const hasAIGrade = !!aiMetadata?.aiScore;
            const isOverriding = hasAIGrade && parseFloat(points) !== aiMetadata.aiScore;

            const questionGrade = {
              questionId,
              pointsAwarded: parseFloat(points) || 0,
              feedback: feedback || ''
            };
            
            // Add override metadata if instructor is changing AI grade
            if (isOverriding) {
              questionGrade.aiGradeMetadata = {
                ...aiMetadata,
                overriddenByInstructor: true,
                overriddenBy: authData.id,
                overriddenAt: new Date().toISOString(),
                overrideReason: grading.overrideReason || 'Instructor adjusted grade',
                originalAIScore: aiMetadata.aiScore,
                originalAIFeedback: aiMetadata.aiFeedback
              };
            }
            
            return questionGrade;
          })
          : [];
        
        // ✅ FIX: Calculate total grade for quiz
        const totalGrade = gradedQuestions.reduce((sum, q) => sum + q.pointsAwarded, 0);
        
        response = await gradeQuiz(authData.id, {
          studentLoginId: submission.loginId,
          courseId: courseId,
          quizId: submission.quizId,
          attemptNumber: submission.attemptNumber,
          gradedQuestions: gradedQuestions,
          overallFeedback: gradeFormData.feedback,
          // ✅ ADD: Include calculated total grade
          grade: totalGrade,
          maxGrade: gradeFormData.maxGrade
        });
      } else if (submission.type === 'exam') {
        const gradedQuestions = gradeFormData.questionGrades 
          ? Object.entries(gradeFormData.questionGrades).map(([questionId, grading]) => {
            // Find the student's answer to check for AI grading
            const override = gradeFormData.answers?.[questionId];
            const points = override?.points !== undefined ? override.points : grading.points;
            const feedback = override?.feedback !== undefined ? override.feedback : grading.feedback;
            const studentAnswer = submission.answers.find(a => a.questionId === questionId);
            const aiMetadata = studentAnswer?.aiGradeMetadata;
            const hasAIGrade = !!aiMetadata?.aiScore;
            const isOverriding = hasAIGrade && parseFloat(points) !== aiMetadata.aiScore;
            
            const questionGrade = {
              questionId,
              pointsAwarded: parseFloat(points) || 0,
              feedback: feedback || ''
            };
            
            // Add override metadata if instructor is changing AI grade
            if (isOverriding) {
              questionGrade.aiGradeMetadata = {
                ...aiMetadata,
                overriddenByInstructor: true,
                overriddenBy: authData.id,
                overriddenAt: new Date().toISOString(),
                overrideReason: grading.overrideReason || 'Instructor adjusted grade',
                originalAIScore: aiMetadata.aiScore,
                originalAIFeedback: aiMetadata.aiFeedback
              };
            }
            
            return questionGrade;
          })
          : [];
        
        // ✅ FIX: Calculate total grade for exam
        const totalGrade = gradedQuestions.reduce((sum, q) => sum + q.pointsAwarded, 0);
        
        response = await gradeExam(authData.id, {
          studentLoginId: submission.loginId,
          courseId: courseId,
          examId: submission.examId,
          attemptNumber: submission.attemptNumber,
          gradedQuestions: gradedQuestions,
          overallFeedback: gradeFormData.feedback,
          // ✅ ADD: Include calculated total grade
          grade: totalGrade,
          maxGrade: gradeFormData.maxGrade
        });
      } else if (submission.type === 'capstone') {
        response = await gradeCapstone(authData.id, {
          studentLoginId: submission.loginId,
          courseId: courseId,
          capstoneId: submission.capstoneId,
          submissionId: submission.submissionId,
          grade: parseFloat(gradeFormData.grade),
          maxGrade: parseFloat(gradeFormData.maxGrade),
          feedback: gradeFormData.feedback,
          gradedBy: authData.id
        });
      }
      
      if (response?.success) {
        showNotification('Grade submitted successfully', 'success');
        closeSubmissionModal();
        await loadPage();
      } else {
        showNotification(response?.message || 'Failed to submit grade', 'error');
      }
    } catch (error) {
      console.error('Failed to submit grade:', error);
      showNotification('An error occurred while submitting grade', 'error');
    }
  };

  const handleGradeAll = async () => {
    const changes = Object.entries(gradingChanges);
    if (changes.length === 0) {
      showNotification('No changes to submit', 'info');
      return;
    }

    try {
      const promises = changes.map(([key, change]) => {
        const [type, ...ids] = key.split('_');
        const submissionId = ids.join('_');

        if (type === 'lab') {
          const submission = labSubmissions.find(s => 
            `${s.loginId}_${s.labId}` === submissionId
          );
          return gradeLab(authData.id, {
            studentLoginId: submission.loginId,
            courseId: courseId,
            labId: submission.labId,
            grade: parseFloat(change.grade),
            maxGrade: parseFloat(change.maxGrade || 100),
            feedback: change.feedback || ''
          });
        }
        // Add similar for quiz, exam, capstone if needed
      });

      await Promise.all(promises);
      showNotification(`Successfully graded ${changes.length} submission(s)`, 'success');
      setGradingChanges({});
      loadPage();
    } catch (error) {
      console.error('Failed to grade all:', error);
      showNotification('Failed to submit some grades', 'error');
    }
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  const groupSubmissionsByItem = (submissions) => {
    return submissions.reduce((acc, submission) => {
      const key = submission.labId || submission.quizId || submission.examId || submission.capstoneId;
      if (!acc[key]) acc[key] = [];
      acc[key].push(submission);
      return acc;
    }, {});
  };

  const calculateMaxPoints = (questions) => {
    if (!questions || questions.length === 0) return 0;
    return questions.reduce((total, question) => {
      return total + (parseFloat(question.points) || 0);
    }, 0);
  };

  // ✅ VALIDATION FUNCTION
  const validateContentBeforeSave = (contentType, items) => {
    const errors = [];
    
    items.forEach((item, idx) => {
      const itemNumber = idx + 1;
      
      if (contentType === 'lessons') {
        if (!item.title?.trim()) errors.push(`Lesson ${itemNumber}: Title is required`);
        if (!item.content?.trim()) errors.push(`Lesson ${itemNumber}: Content is required`);
        //if (item.order === undefined || item.order === null) errors.push(`Lesson ${itemNumber}: Order is required`);
      }
      
      if (contentType === 'labs') {
        if (!item.title?.trim()) errors.push(`Lab ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Lab ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Lab ${itemNumber}: Lab Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Lab ${itemNumber}: Lab duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Lab ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (!q.questionType) errors.push(`Lab ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Lab ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code, performance
          if (!['essay', 'code', 'performance', 'lab_practical'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Lab ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'quizzes') {
        if (!item.title?.trim()) errors.push(`Quiz ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Quiz ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Quiz ${itemNumber}: Quiz Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Quiz ${itemNumber}: Quiz duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Quiz ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (!q.questionType) errors.push(`Quiz ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Quiz ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code
          if (!['essay', 'code'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Quiz ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'exams') {
        if (!item.title?.trim()) errors.push(`Exam ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Exam ${itemNumber}: Description is required`);
        if (!String(item.dueDate || '').trim()) errors.push(`Exam ${itemNumber}: Exam Due Date is required`);
        if (!String(item.duration || '').trim()) errors.push(`Exam ${itemNumber}: Exam duration is required`);
        if (!item.duration) errors.push(`Exam ${itemNumber}: Duration is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Exam ${itemNumber}: Grade Weight is required`);
        
        // Validate questions
        item.questions?.forEach((q, qIdx) => {
          const qNum = qIdx + 1;
          //if (!q.questionId?.trim()) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question ID is required`);
          if (!q.questionText?.trim()) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question Text is required`);
          if (!q.questionType) errors.push(`Exam ${itemNumber}, Question ${qNum}: Question Type is required`);
          if (q.points === undefined || q.points === null || q.points === '') errors.push(`Exam ${itemNumber}, Question ${qNum}: Points is required`);
          
          // Correct answer required except for essay, code
          if (!['essay', 'code'].includes(q.questionType) && !q.correctAnswer?.trim()) {
            errors.push(`Exam ${itemNumber}, Question ${qNum}: Correct Answer is required`);
          }
        });
      }
      
      if (contentType === 'capstones') {
        if (!item.capstoneId?.trim()) errors.push(`Capstone ${itemNumber}: Capstone ID is required`);
        if (!item.title?.trim()) errors.push(`Capstone ${itemNumber}: Title is required`);
        // if (!item.description?.trim()) errors.push(`Capstone ${itemNumber}: Description is required`);
        if (item.maxGrade === undefined || item.maxGrade === null) errors.push(`Capstone ${itemNumber}: Max Points is required`);
        if (!String(item.gradeWeight || '').trim()) errors.push(`Capstone ${itemNumber}: Grade Weight is required`);
      }
    });
    
    return errors;
  };

  const handleContentFileUpload = (contentType, files) => {
    setEditedContentFiles(prev => ({
      ...prev,
      [contentType]: files
    }));
  };

  const removeContentFile = (contentType, fileIndex) => {
    setEditedContentFiles(prev => {
      const updated = { ...prev };
      if (updated[contentType]) {
        const newFiles = Array.from(updated[contentType]);
        newFiles.splice(fileIndex, 1);
        if (newFiles.length === 0) {
          delete updated[contentType];
        } else {
          updated[contentType] = newFiles;
        }
      }
      return updated;
    });
  };

  const uploadContentFiles = async (contentType, contentId) => {
    const files = editedContentFiles[contentType];
    
    if (!files || files.length === 0) {
      return { fileIds: [], fileNames: [] };
    }

    setUploadingContentFiles(true);
    const uploadedFileIds = [];
    const uploadedFileNames = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        if (file.size > 10 * 1024 * 1024) {
          showNotification(`File ${file.name} exceeds 10MB limit`, 'error');
          continue;
        }

        const allowedTypes = ['application/pdf', 'application/msword', 
                            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                            'text/plain', 'image/png', 'image/jpeg', 'application/zip'];
        if (!allowedTypes.includes(file.type)) {
          showNotification(`File ${file.name} has invalid type`, 'error');
          continue;
        }

        setContentFileUploadProgress(prev => ({
          ...prev,
          [contentType]: `Uploading ${i + 1}/${files.length}...`
        }));

        const fileExtension = file.name.split('.').pop();
        const structuredFilename = `${authData.id}/${courseId}/${contentType}/${contentId}/resource_${Date.now()}_${i + 1}.${fileExtension}`;

        const metadata = {
          instructorId: authData.id,
          courseId: courseId,
          contentType: contentType,
          contentId: contentId,
          uploadedBy: authData.id,
          originalFilename: file.name,
          structuredFilename: structuredFilename
        };

        const uploadResult = await uploadFile(file, metadata);

        if (uploadResult.success) {
          uploadedFileIds.push(uploadResult.data.fileId);
          uploadedFileNames.push(file.name);
        } else {
          showNotification(`Failed to upload ${file.name}`, 'error');
        }
      }

      setContentFileUploadProgress(prev => ({
        ...prev,
        [contentType]: 'Complete'
      }));

      return { fileIds: uploadedFileIds, fileNames: uploadedFileNames };
    } catch (error) {
      console.error('Error uploading content files:', error);
      showNotification('Failed to upload some files', 'error');
      return { fileIds: uploadedFileIds, fileNames: uploadedFileNames };
    } finally {
      setUploadingContentFiles(false);
      setContentFileUploadProgress({});
    }
  };

  const handleStageFiles = (contentType, files) => {
    setEditedContentFiles(prev => ({
      ...prev,
      [contentType]: Array.from(files)
    }));
  };

  const labGroups = groupSubmissionsByItem(labSubmissions);
  const quizGroups = groupSubmissionsByItem(quizSubmissions);
  const examGroups = groupSubmissionsByItem(examSubmissions);
  const capstoneGroups = groupSubmissionsByItem(capstoneSubmissions);

  const renderGradingSection = (groups, type, expandedState, icon, title) => {
    return (
      <div className="grading-category">
        <div className="grading-category-header">
          <h3>{icon} {title} ({Object.keys(groups).length})</h3>
        </div>

        {Object.keys(groups).length > 0 ? (
          Object.entries(groups).map(([itemId, submissions]) => {
            const item = courseInfo?.[`${type}s`]?.find(i => i[`${type}Id`] === itemId) || 
                        (type === 'lab' && courseInfo?.labs?.find(l => l.labId === itemId));
            const itemTitle = item?.title || itemId;
            const pendingCount = submissions.filter(s => s.status === 'submitted').length;
            const gradedCount = submissions.filter(s => s.status === 'graded').length;
            const isExpanded = expandedState[itemId];

            return (
              <div key={itemId} className="grading-item-card">
                <div 
                  onClick={() => toggleExpand(type, itemId)}
                  className={`grading-item-header ${isExpanded ? 'expanded' : ''}`}
                >
                  <div>
                    <div className="grading-item-title">
                      <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                      <strong>{itemId}: {itemTitle}</strong>
                    </div>
                    <div className="grading-item-meta">
                      {submissions.length} submission{submissions.length !== 1 ? 's' : ''} • 
                      <span className="pending-count"> {pendingCount} pending</span> • 
                      <span className="graded-count"> {gradedCount} graded</span>
                    </div>
                  </div>
                </div>

                {/* Table Header Preview (shown when collapsed) */}
                {!isExpanded && (
                  <div className="table-header-preview">
                    <div className="preview-column">Student</div>
                    <div className="preview-column">Submitted</div>
                    <div className="preview-column">Status</div>
                    <div className="preview-column">Grade</div>
                    <div className="preview-column">Max Points</div>
                    <div className="preview-column">Feedback</div>
                    <div className="preview-column">Action</div>
                  </div>
                )}

                {/* Full Table (shown when expanded) */}
                {isExpanded && (
                  <div className="submissions-table-container">
                    <table className="submissions-table">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Submitted</th>
                          <th className="center">Status</th>
                          <th className="center">Grade</th>
                          <th className="center">Max Points</th>
                          <th className="center">Feedback</th>
                          <th className="center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {submissions.map((submission, idx) => {
                          const submissionKey = `${type}_${submission.loginId}_${itemId}`;
                          const hasChanges = gradingChanges[submissionKey];
                          
                          return (
                          <tr key={idx} className={hasChanges ? 'has-changes' : ''}>
                            <td>
                              <div className="student-info">{submission.studentName}</div>
                              <div className="student-login">{submission.loginId}</div>
                            </td>
                            <td className="submission-time">
                              {submission.completedAt ? getTimeAgo(submission.completedAt) : '—'}
                            </td>
                            <td className="text-center">
                              <span className={`status-badge ${
                                submission.status === 'graded' ? 'graded' :
                                submission.status === 'submitted' ? 'pending' : 'not-submitted'
                              }`}>
                                {submission.status === 'graded' ? '✅ Graded' :
                                submission.status === 'submitted' ? '⏳ Pending' : '📭 Not Submitted'}
                              </span>
                            </td>
                            <td className="text-center">
                              {submission.status !== 'not_submitted' ? (
                                <input
                                  type="number"
                                  className="grade-input"
                                  value={hasChanges?.grade ?? submission.grade ?? ''}
                                  onChange={(e) => handleInlineGradeChange(submissionKey, 'grade', e.target.value)}
                                  placeholder="0"
                                />
                              ) : '—'}
                            </td>
                            <td className="text-center">
                              {submission.maxGrade ?? '—'}
                            </td>
                            <td className="text-center">
                              {submission.status !== 'not_submitted' ? (
                                <input
                                  type="text"
                                  className="feedback-input"
                                  value={hasChanges?.feedback ?? submission.feedback ?? ''}
                                  onChange={(e) => handleInlineGradeChange(submissionKey, 'feedback', e.target.value)}
                                  placeholder="Add feedback"
                                />
                              ) : '—'}
                            </td>
                            
                            <td className="text-center" style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                                {/* {submission.status !== 'not_submitted' && ( */}
                                    <button
                                        onClick={() => openSubmissionModal({ ...submission, type, itemTitle })}
                                        className="view-details-btn"
                                    >
                                        👁 View Submission Details
                                    </button>
                                {/* )} */}
                                {/* {submission.status === 'not_submitted' && ( */}
                                  {/* <button
                                      onClick={() => handleViewAsStudent(
                                          submission.loginId,
                                          submission.studentName,
                                          type,
                                          submission[`${type}Id`]
                                      )}
                                      className="view-details-btn"
                                      style={{ background: '#0ea5e9', color: 'white' }}
                                  >
                                      👁 PreView
                                  </button> */}
                                {/* )} */}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p className="no-submissions">No {type} submissions yet</p>
        )}
      </div>
    );
  };

  // const handleEditContent = () => {
  //   setIsEditMode(true);
  //   if (selectedContentType && courseInfo?.[selectedContentType] && selectedItemId) {
  //     // Find the specific item to edit based on selectedItemId
  //     const idField = selectedContentType === 'lessons' ? 'lessonId' : 
  //                     selectedContentType === 'labs' ? 'labId' :
  //                     selectedContentType === 'quizzes' ? 'quizId' :
  //                     selectedContentType === 'exams' ? 'examId' : 'capstoneId';
      
  //     const itemToEdit = courseInfo[selectedContentType].find(
  //       item => item[idField] === selectedItemId
  //     );
      
  //     if (itemToEdit) {
  //       // Deep copy only the selected item
  //       const cleanedItem = JSON.parse(JSON.stringify(itemToEdit));
        
  //       // ✅ NEW: Clean up questions based on their type
  //       if (cleanedItem.questions && Array.isArray(cleanedItem.questions)) {
  //         cleanedItem.questions = cleanedItem.questions.map(question => {
  //           const cleanQuestion = { ...question };
            
  //           // Remove fields that don't belong to this question type
  //           switch(cleanQuestion.questionType) {
  //             case 'multiple_choice':
  //               delete cleanQuestion.iotRequirements;
  //               delete cleanQuestion.evidence;
  //               delete cleanQuestion.labRequirements;
  //               break;
  //             case 'true_false':
  //             case 'short_answer':
  //             case 'essay':
  //             case 'code':
  //             case 'performance':
  //               delete cleanQuestion.options;
  //               delete cleanQuestion.iotRequirements;
  //               delete cleanQuestion.evidence;
  //               delete cleanQuestion.labRequirements;
  //               break;                    
  //             case 'lab_practical':
  //               delete cleanQuestion.options;
  //               if (!cleanQuestion.iotRequirements) cleanQuestion.iotRequirements = [];
  //               if (!cleanQuestion.labRequirements) cleanQuestion.labRequirements = '';
  //               break; 
  //             default:
  //               delete cleanQuestion.options;
  //               delete cleanQuestion.iotRequirements;
  //               delete cleanQuestion.evidence;
  //               delete cleanQuestion.labRequirements;  
  //           }
            
  //           return cleanQuestion;
  //         });
  //       }
        
  //       setEditedContent([cleanedItem]);
  //     } else {
  //       showNotification('Item not found', 'error');
  //     }
  //   }
  // };

  // const handleSaveEdit = async () => {
  //   if (!editedContent || !selectedContentType || editedContent.length === 0) {
  //     showNotification('No changes to save', 'info');
  //     return;
  //   }

  //   // ✅ VALIDATE BEFORE SAVING
  //   const validationErrors = validateContentBeforeSave(selectedContentType, editedContent);
  //   if (validationErrors.length > 0) {
  //     const errorMessage = validationErrors.join('\n');
  //     showNotification(`Please fix the following errors:\n${errorMessage}`, 'error');
      
  //     // Also show in console for detailed view
  //     console.error('Validation Errors:', validationErrors);
      
  //     // Alert for better visibility
  //     alert(`Please fill out all required fields:\n\n${errorMessage}`);
  //     return;
  //   }

  //   try {
  //     const contentTypeMap = {
  //       'lessons': { singular: 'lesson', idField: 'lessonId' },
  //       'labs': { singular: 'lab', idField: 'labId' },
  //       'quizzes': { singular: 'quiz', idField: 'quizId' },
  //       'exams': { singular: 'exam', idField: 'examId' },
  //       'capstones': { singular: 'capstone', idField: 'capstoneId' }
  //     };

  //     const { singular: contentType, idField } = contentTypeMap[selectedContentType];

  //     // Since we're only editing ONE item, editedContent[0] is that item
  //     const item = editedContent[0];
  //     const contentId = item[idField];
      
  //     // Prepare update data (remove the ID field from updates)
  //     const { [idField]: _, ...updateData } = item;
      
  //     // Add updatedBy field
  //     updateData.updatedBy = authData.id;

  //     // ✅ NEW: Upload files if any (only for lessons and labs)
  //     if (selectedContentType === 'lessons' || selectedContentType === 'labs') {
  //       const uploadResult = await uploadContentFiles(selectedContentType, contentId);
        
  //       if (uploadResult.fileIds.length > 0) {
  //         // Add uploaded files to existing files
  //         const existingFileIds = updateData.resourceFileIds || [];
  //         const existingFileNames = updateData.resourceFileNames || [];
          
  //         updateData.resourceFileIds = [...existingFileIds, ...uploadResult.fileIds];
  //         updateData.resourceFileNames = [...existingFileNames, ...uploadResult.fileNames];
  //       }
  //     }

  //     // ✅ ADD THIS: Convert numeric fields from strings to numbers
  //     if (updateData.duration !== undefined) {
  //       updateData.duration = Number(updateData.duration);
  //     }
  //     if (updateData.maxGrade !== undefined) {
  //       updateData.maxGrade = Number(updateData.maxGrade);
  //     }
  //     if (updateData.gradeWeight !== undefined) {
  //       updateData.gradeWeight = Number(updateData.gradeWeight);
  //     }
  //     if (updateData.attemptsAllowed !== undefined) {
  //       updateData.attemptsAllowed = Number(updateData.attemptsAllowed);
  //     }
  //     if (updateData.order !== undefined) {
  //       updateData.order = Number(updateData.order);
  //     }

  //     if (selectedContentType === 'labs' || selectedContentType === 'quizzes' || selectedContentType === 'exams') {
  //       if (updateData.questions && updateData.questions.length > 0) {
  //         updateData.maxGrade = calculateMaxPoints(updateData.questions);
  //       }
  //     }
      
  //     console.log("\n📤 FINAL PAYLOAD:");
  //     console.log("update course to: ", {
  //       courseId,
  //       contentType,
  //       contentId,
  //       updateData: JSON.stringify(updateData, null, 2),
  //       gradeweight: typeof(updateData.gradeWeight)
  //     });
  //     console.log("=== SAVE DEBUG END ===\n");
      
  //     // Call the API to update this specific content item
  //     const response = await updateCourseContent(
  //       courseId,
  //       contentType,
  //       contentId,
  //       updateData
  //     );

  //     if (response.success) {
  //       showNotification(
  //         `${contentType.charAt(0).toUpperCase() + contentType.slice(1)} updated successfully`,
  //         'success'
  //       );
  //       setIsEditMode(false);
  //       setEditedContent(null);
  //       setEditedContentFiles({});  // ✅ Clear uploaded files
  //       await loadPage(); // Reload the data
  //     } else {
  //       showNotification('Failed to update content', 'error');
  //     }
  //   } catch (error) {
  //     console.error('=== SAVE ERROR ===');
  //     console.error('Error details:', error);
  //     console.error('Error message:', error.message);
  //     console.error('Error stack:', error.stack);
  //     showNotification('Failed to update content', 'error');
  //   }
  // };

  // // Add this function to handle field changes
  // const handleFieldChange = (index, field, value) => {
  //   const updated = [...editedContent];
  //   updated[index][field] = value;
  //   setEditedContent(updated);
  // };

  // // Add this function to handle question changes (for quizzes/exams)
  // const handleQuestionChange = (itemIndex, questionIndex, field, value) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex][field] = value;
  //   setEditedContent(updated);
  // };

  // // Add this new function after handleQuestionChange (around line 1830)
  // const handleQuestionTypeChange = (itemIndex, questionIndex, newType) => {
  //   const updated = [...editedContent];
  //   const question = updated[itemIndex].questions[questionIndex];
    
  //   // Remove fields that don't belong to any question type
  //   const fieldsToRemove = ['options', 'iotRequirements', 'evidence', 'labRequirements'];
  //   fieldsToRemove.forEach(field => {
  //     delete question[field];
  //   });
    
  //   // Set the new type
  //   question.questionType = newType;
    
  //   // Add required fields based on the new type
  //   switch(newType) {
  //     case 'multiple_choice':
  //       question.options = ['', '', '', ''];
  //       question.correctAnswer = '';
  //       break;
        
  //     case 'true_false':
  //       question.correctAnswer = '';
  //       break;
        
  //     case 'short_answer':
  //     case 'essay':
  //     case 'code':
  //       question.correctAnswer = '';
  //       break;
        
  //     case 'lab_practical':
  //       question.correctAnswer = '';
  //       question.iotRequirements = [];
  //       question.labRequirements = '';
  //       break;
        
  //     case 'performance':
  //       question.correctAnswer = '';
  //       break;
        
  //     default:
  //       question.correctAnswer = '';
  //   }
    
  //   setEditedContent(updated);
  // };

  // // Add this function after handleRemoveQuestion
  // const handleRequirementChange = (itemIndex, questionIndex, reqIndex, field, value) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex].iotRequirements[reqIndex][field] = value;
    
  //   // ✅ IF POINTS CHANGED, RECALCULATE QUESTION TOTAL
  //   if (field === 'points') {
  //     const totalPoints = updated[itemIndex].questions[questionIndex].iotRequirements.reduce(
  //       (sum, req) => sum + (parseFloat(req.points) || 0), 
  //       0
  //     );
  //     updated[itemIndex].questions[questionIndex].points = totalPoints;
  //   }
    
  //   setEditedContent(updated);
  // };

  // // Add this function to handle option changes (for quiz/exam questions)
  // const handleOptionChange = (itemIndex, questionIndex, optionIndex, value) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex].options[optionIndex] = value;
  //   setEditedContent(updated);
  // };

  // // Add a new question to a quiz/exam
  // const handleAddQuestion = (itemIndex) => {
  //   const updated = [...editedContent];

  //   // Get existing questions to determine the next question number
  //   const existingQuestions = updated[itemIndex].questions || [];
  
  //   // Extract question numbers from existing questionIds and find the max
  //   let maxQuestionNum = 0;
  //   existingQuestions.forEach(q => {
  //     // Extract number from questionId (e.g., "Q5" -> 5)
  //     const match = q.questionId.match(/Q(\d+)/);
  //     if (match) {
  //       const num = parseInt(match[1]);
  //       if (num > maxQuestionNum) {
  //         maxQuestionNum = num;
  //       }
  //     }
  //   });
    
  //   // Generate new consecutive ID
  //   const newQuestionId = `Q${maxQuestionNum + 1}`;
    
  //   const newQuestion = {
  //     questionId: newQuestionId, //`Q${Date.now()}`, // Temporary ID
  //     questionText: '',
  //     questionType: 'multiple_choice',
  //     options: ['', '', '', ''],
  //     // correctAnswer: '',
  //     points: 0,
  //     // explanation: ''
  //   };
    
  //   if (!updated[itemIndex].questions) {
  //     updated[itemIndex].questions = [];
  //   }
  //   updated[itemIndex].questions.push(newQuestion);
  //   setEditedContent(updated);
  // };

  // // Remove a question from a quiz/exam
  // const handleRemoveQuestion = (itemIndex, questionIndex) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions.splice(questionIndex, 1);
  //   setEditedContent(updated);
  // };

  // // Add an option to a multiple choice question
  // const handleAddOption = (itemIndex, questionIndex) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex].options.push('');
  //   setEditedContent(updated);
  // };

  // // Remove an option from a multiple choice question
  // const handleRemoveOption = (itemIndex, questionIndex, optionIndex) => {
  //   const updated = [...editedContent];
  //   updated[itemIndex].questions[questionIndex].options.splice(optionIndex, 1);
  //   setEditedContent(updated);
  // };

  // Replace the openContentModal function with this:
  
  const openListModal = (contentType) => {
    setSelectedListType(contentType);
    setShowListModal(true);
  };

  const closeListModal = () => {
    setShowListModal(false);
    setSelectedListType(null);
  };

  const openContentModalFromList = (contentType, itemId) => {
    setSelectedContentType(contentType);
    setSelectedItemId(itemId);
    setShowContentModal(true);
    setShowListModal(false);
  };

  const getStudentSubmissions = (itemId, type) => {
    if (!courseProgress) return [];
    
    if (type === 'lab') {
      const labProgress = courseProgress.labProgress?.find(lp => lp.courseId === courseId);
      return labProgress?.labs?.filter(lab => lab.labId === itemId) || [];
    } else if (type === 'quiz') {
      const quizProgress = courseProgress.quizProgress?.find(qp => qp.courseId === courseId);
      const quiz = quizProgress?.quizzes?.find(q => q.quizId === itemId);
      return quiz?.attempts || [];
    } else if (type === 'exam') {
      const examProgress = courseProgress.examProgress?.find(ep => ep.courseId === courseId);
      const exam = examProgress?.exams?.find(e => e.examId === itemId);
      return exam?.attempts || [];
    } else if (type === 'capstone') {
      const capstoneProject = courseProgress.capstoneProjects?.find(cp => cp.courseId === courseId);
      const capstone = capstoneProject?.capstones?.find(c => c.capstoneId === itemId);
      return capstone?.submissions || [];
    }
    
    return [];
  };

  const handleDeleteItem = async (contentType, itemId) => {
    if (!window.confirm(`Are you sure you want to delete this ${contentType.slice(0, -1)}?`)) {
      return;
    }

    try {
      const removeMap = {
        'lessons': removeLesson,
        'labs': removeLab,
        'quizzes': removeQuiz,
        'exams': removeExam,
        'capstones': removeCapstone
      };

      const response = await removeMap[contentType](courseId, itemId, authData.id, authData.name);
      
      if (response.success) {
        showNotification(`${contentType.slice(0, -1)} deleted successfully`, 'success');
        await loadPage();
      } else {
        showNotification('Failed to delete', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('An error occurred', 'error');
    }
  };

  const handleAddNewItem = async (contentType) => {
    // Generate new ID
    const existingItems = courseInfo?.[contentType] || [];
    const idField = contentType === 'lessons' ? 'lessonId' : 
                    contentType === 'labs' ? 'labId' :
                    contentType === 'quizzes' ? 'quizId' :
                    contentType === 'exams' ? 'examId' : 'capstoneId';
    
    const prefix = contentType === 'lessons' ? 'LESSON' : 
                  contentType === 'labs' ? 'LAB' :
                  contentType === 'quizzes' ? 'QUIZ' :
                  contentType === 'exams' ? 'EXAM' : 'CAP';
    
    const newId = `${prefix}${existingItems.length + 1}`;
    
    // Create default item based on type
    let newItem = {};
    
    if (contentType === 'lessons') {
      newItem = {
        title: `New Lesson ${existingItems.length + 1}`,
        content: 'Lesson content here',
        order: existingItems.length + 1,
        duration: 60,
        videoUrl: '',
        resources: []
      };
    } else if (contentType === 'labs') {
      newItem = {
        title: `New Lab ${existingItems.length + 1}`,
        description: '',
        order: existingItems.length + 1,
        instructions: '',
        duration: 120,
        resources: [],
        questions: [],
        maxGrade: 0,
        gradeWeight: 0.1,
        dueDate: new Date().toISOString().split('T')[0],
        isPublished: false
      };
    } else if (contentType === 'quizzes') {
      newItem = {
        title: `New Quiz ${existingItems.length + 1}`,
        description: 'Quiz description',
        order: existingItems.length + 1,
        questions: [],
        duration: 30,
        maxGrade: 0,
        gradeWeight: 0.1,
        attemptsAllowed: 3,
        isPublished: false
      };
    } else if (contentType === 'exams') {
      newItem = {
        title: `New Exam ${existingItems.length + 1}`,
        description: 'Exam description',
        order: existingItems.length + 1,
        questions: [],
        duration: 120,
        maxGrade: 0,
        gradeWeight: 0.3,
        attemptsAllowed: 1,
        dueDate: new Date().toISOString().split('T')[0],
        isPublished: false,
        isFinalExam: false
      };
    } else if (contentType === 'capstones') {
      newItem = {
        title: `New Capstone ${existingItems.length + 1}`,
        description: 'Capstone description',
        order: existingItems.length + 1,
        questions: [],
        duration: 120,
        maxGrade: 0,
        gradeWeight: 0.3,
        attemptsAllowed: 1,
        dueDate: new Date().toISOString().split('T')[0],
        isPublished: false,
      };
    }

    try {
      const addMap = {
        'lessons': addLesson,
        'labs': addLab,
        'quizzes': addQuiz,
        'exams': addExam,
        'capstones': addCapstone
      };

      const response = await addMap[contentType](courseId, newId, newItem);
      
      if (response.success) {
        showNotification(`${contentType.slice(0, -1)} added successfully`, 'success');
        await loadPage();
      } else {
        showNotification('Failed to add item', 'error');
      }
    } catch (error) {
      console.error('Add error:', error);
      showNotification('An error occurred', 'error');
    }
  };

  const closeContentModal = () => {
    setShowContentModal(false);
    setSelectedContentType(null);
    setSelectedItemId(null);
    setIsEditMode(false);
    setEditedContent(null);
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      await triggerDownload(fileId, filename, authData.id);
      showNotification('File download started', 'success');
    } catch (error) {
      console.error('Download error:', error);
      showNotification('Failed to download file', 'error');
    }
  };

  const onWeightEdit = (type, id, currentWeight) => {
    setEditingWeight({ type, id });
    setTempWeight((currentWeight * 100).toString());
  };

  const onWeightCancel = () => {
    setEditingWeight(null);
    setTempWeight('');
  };

  const onTempWeightChange = (val) => setTempWeight(val);

  const onWeightSave = async () => {
    if (!editingWeight) return;
    const { type, id } = editingWeight;
    const newWeight = parseFloat(tempWeight) / 100;
    if (isNaN(newWeight) || newWeight < 0 || newWeight > 1) {
      showNotification('Invalid weight. Must be between 0 and 100%', 'error');
      return;
    }
    try {
      const response = await updateCourseContent(
        courseId,
        type,
        id,
        { gradeWeight: newWeight, updatedBy: authData.id }
      );
      if (response.success) {
        showNotification('Grade weight updated successfully', 'success');
        setEditingWeight(null);
        setTempWeight('');
        await loadPage();
      } else {
        showNotification('Failed to update grade weight', 'error');
      }
    } catch (error) {
      console.error('Error updating grade weight:', error);
      showNotification('An error occurred', 'error');
    }
  };

  // Generate AI Rubric
  const handleGenerateRubric = async (itemIdx, questionIdx) => {
    const question = editedContent[itemIdx].questions[questionIdx];
    
    if (!question.questionText || !question.points) {
      showNotification('Please fill in question text and points first', 'error');
      return;
    }

    setGeneratingRubric(true);
    setRubricQuestionIndex({ itemIdx, questionIdx });
    
    try {
      const response = await generateQuestionRubric(
        authData?.id,
        {
          questionText: question.questionText,
          questionType: question.questionType,
          expectedAnswer: question.correctAnswer || '',
          maxPoints: question.points
        }
      );

      if (response.success) {
        setRubricPreview(response.data);
        // ✅ NEW: Initialize editable rubric as a deep copy
        setEditableRubric(JSON.parse(JSON.stringify(response.data)));
        setIsEditingRubric(true); // ✅ NEW: Enter edit mode
        showNotification('Rubric generated successfully!', 'success');
      } else {
        showNotification('Failed to generate rubric', 'error');
      }
    } catch (error) {
      console.error('Rubric generation error:', error);
      showNotification('Error generating rubric', 'error');
    } finally {
      setGeneratingRubric(false);
    }
  };

  // ✅ NEW: Handle percentage change for a criterion
  const handleCriterionPercentageChange = (criterionKey, newPercentage) => {
    const percentage = parseInt(newPercentage) || 0;
    
    setEditableRubric(prev => {
      const updated = { ...prev };
      const maxPoints = editedContent[rubricQuestionIndex.itemIdx].questions[rubricQuestionIndex.questionIdx].points;
      
      // Update percentage and recalculate max points
      updated.rubric[criterionKey].percentage = percentage;
      updated.rubric[criterionKey].max = parseFloat(((percentage / 100) * maxPoints).toFixed(2));
      
      return updated;
    });
  };

  // ✅ NEW: Handle description field changes (description, fullCredit, partialCredit, noCredit)
  const handleCriterionFieldChange = (criterionKey, field, value) => {
    setEditableRubric(prev => {
      const updated = { ...prev };
      updated.rubric[criterionKey][field] = value;
      return updated;
    });
  };

  // ✅ NEW: Handle criterion name change
  const handleCriterionNameChange = (oldKey, newKey) => {
    // Prevent empty or duplicate names
    const sanitizedKey = newKey.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    
    if (!sanitizedKey || sanitizedKey === oldKey) return;
    if (editableRubric.rubric[sanitizedKey]) {
      showNotification('Criterion name already exists', 'error');
      return;
    }
    
    setEditableRubric(prev => {
      const updated = { ...prev };
      // Create new key with same values
      updated.rubric[sanitizedKey] = { ...updated.rubric[oldKey] };
      // Delete old key
      delete updated.rubric[oldKey];
      return updated;
    });
  };

  // ✅ NEW: Add new criterion
  const handleAddCriterion = () => {
    const maxPoints = editedContent[rubricQuestionIndex.itemIdx].questions[rubricQuestionIndex.questionIdx].points;
    
    let criterionName = 'new_criterion';
    let counter = 1;
    while (editableRubric.rubric[criterionName]) {
      criterionName = `new_criterion_${counter}`;
      counter++;
    }
    
    setEditableRubric(prev => {
      const updated = { ...prev };
      updated.rubric[criterionName] = {
        max: 0,
        percentage: 0,
        description: 'Description for this criterion',
        fullCredit: 'Criteria for full credit',
        partialCreditHigh: 'Criteria for high partial credit (70-85%)',
        partialCreditLow: 'Criteria for low partial credit (40-65%)',
        noCredit: 'Criteria for no credit'
      };
      return updated;
    });
  };

  // ✅ NEW: Remove criterion
  const handleRemoveCriterion = (criterionKey) => {
    // Prevent removing if only one criterion left
    if (Object.keys(editableRubric.rubric).length <= 1) {
      showNotification('Cannot remove the last criterion', 'error');
      return;
    }
    
    setEditableRubric(prev => {
      const updated = { ...prev };
      delete updated.rubric[criterionKey];
      return updated;
    });
  };

  // ✅ NEW: Handle rubric text (summary) change
  const handleRubricTextChange = (newText) => {
    setEditableRubric(prev => ({
      ...prev,
      rubricText: newText
    }));
  };

  // ✅ NEW: Calculate total percentage
  const calculateTotalPercentage = () => {
    if (!editableRubric?.rubric) return 0;
    return Object.values(editableRubric.rubric).reduce((sum, criterion) => sum + (criterion.percentage || 0), 0);
  };

  // ✅ NEW: Validate rubric before accepting
  const validateRubric = () => {
    const totalPercentage = calculateTotalPercentage();
    
    if (totalPercentage !== 100) {
      showNotification(`Total percentage must equal 100% (currently ${totalPercentage}%)`, 'error');
      return false;
    }
    
    // Check all criteria have descriptions
    for (const [key, criterion] of Object.entries(editableRubric.rubric)) {
      if (!criterion.description || !criterion.fullCredit || !criterion.noCredit) {
        showNotification(`Criterion "${key}" is missing required fields`, 'error');
        return false;
      }
    }
    
    return true;
  };

  // ✅ NEW: Cancel editing
  const handleCancelEdit = () => {
    // Cancel rubric editing
    setEditableRubric(null);
    setIsEditingRubric(false);
    setRubricPreview(null);
    setRubricQuestionIndex(null);
    
    // Cancel content editing
    setIsEditMode(false);
    setEditedContent(null);
    setEditedContentFiles({});
  };

  const handleResetToOriginal = () => {
    if (rubricPreview) {
      setEditableRubric(JSON.parse(JSON.stringify(rubricPreview)));
      showNotification('Reset to AI-generated rubric', 'info');
    }
  };

  // ✅ UPDATED: Accept the edited rubric
  const handleAcceptRubric = () => {
    // Validate before accepting
    if (!validateRubric()) {
      return;
    }
    
    const { itemIdx, questionIdx } = rubricQuestionIndex;
    
    // Apply the edited rubric to the question
    const updatedContent = [...editedContent];
    updatedContent[itemIdx].questions[questionIdx].aiGradeMetadata = {
      rubric: editableRubric.rubric,
      rubricText: editableRubric.rubricText,
      questionType: editableRubric.questionType,
      maxPoints: editableRubric.maxPoints
    };
    
    setEditedContent(updatedContent);
    
    // Clear editing states
    setEditableRubric(null);
    setIsEditingRubric(false);
    setRubricPreview(null);
    setRubricQuestionIndex(null);
    
    showNotification('Rubric applied successfully!', 'success');
  };

  // ✅ UPDATED: Reject rubric (same as cancel)
  const handleRejectRubric = () => {
    handleCancelEdit();
    showNotification('Rubric discarded', 'info');
  };

  const toggleRubric = (questionId) => {
    setExpandedRubrics(prev => ({
        ...prev,
        [questionId]: !prev[questionId]
    }));
  };

  // const renderRubricGenerator = (question, itemIdx, qIdx) => {
  //   if (!['essay', 'code', 'short_answer'].includes(question.questionType)) {
  //     return null;
  //   }

  //   const isCurrentQuestion = rubricQuestionIndex?.itemIdx === itemIdx && rubricQuestionIndex?.questionIdx === qIdx;
  //   const totalPercentage = isCurrentQuestion && editableRubric ? calculateTotalPercentage() : 0;

  //   return (
  //     <div className="ai-rubric-section">
  //       {/* Generate Button */}
  //       {!isEditingRubric || !isCurrentQuestion ? (
  //         <>
  //         {question.aiGradeMetadata && 
  //           <span className="rubric-badge">✓ AI Rubric Applied</span>
  //         }
  //         <button
  //           type="button"
  //           onClick={() => handleGenerateRubric(itemIdx, qIdx)}
  //           disabled={generatingRubric || !question.questionText || !question.points}
  //           className="btn-generate-rubric"
  //         >
  //         {
  //           generatingRubric && isCurrentQuestion
  //             ? '⏳ Generating...'
  //             : (!question.aiGradeMetadata
  //                 ? '🤖 Generate AI Rubric'
  //                 : '🤖 Regenerate AI Rubric'
  //               )
  //         }
  //         </button>
  //         </>
  //       ) : null}

  //       {/* {question.aiGradeMetadata && (!isEditingRubric || !isCurrentQuestion) && (
  //         <div className="applied-rubric-indicator">
  //           <span className="rubric-badge">✓ AI Rubric Applied</span>
  //           <button
  //             type="button"
  //             onClick={() => handleGenerateRubric(itemIdx, qIdx)}
  //             className="btn-regenerate"
  //           >
  //             🔄 Regenerate
  //           </button>
  //         </div>
  //       )} */}
        
  //       {/* Editable Rubric Editor */}
  //       {isEditingRubric && isCurrentQuestion && editableRubric && (
  //         <div className="rubric-editor-box">
  //           <div className="rubric-editor-header">
  //             <h5>📝 Edit AI-Generated Rubric</h5>
  //             <span className="edit-hint">Customize before accepting</span>
  //           </div>
            
  //           {/* Rubric Summary Text */}
  //           <div className="rubric-summary-editor">
  //             <label>📋 Summary Description:</label>
  //             <textarea 
  //               value={editableRubric.rubricText || ''}
  //               onChange={(e) => handleRubricTextChange(e.target.value)}
  //               className="edit-textarea"
  //               rows="3"
  //               placeholder="Overall rubric description..."
  //             />
  //           </div>
            
  //           {/* Editable Criteria */}
  //           <div className="rubric-criteria-editor">
  //             <label className="criteria-label">Grading Criteria:</label>
              
  //             {Object.entries(editableRubric.rubric || {}).map(([criterionKey, criterion]) => (
  //               <div key={criterionKey} className="criterion-editor-card">
                  
  //                 {/* Criterion Header with Name and Remove Button */}
  //                 <div className="criterion-header">
  //                   <input 
  //                     type="text" 
  //                     value={criterionKey.replace(/_/g, ' ').toUpperCase()}
  //                     onChange={(e) => {
  //                       const newKey = e.target.value.toLowerCase().replace(/\s+/g, '_');
  //                       if (newKey !== criterionKey) {
  //                         handleCriterionNameChange(criterionKey, newKey);
  //                       }
  //                     }}
  //                     className="criterion-name-input"
  //                     placeholder="Criterion name"
  //                   />
  //                   <button
  //                     type="button"
  //                     onClick={() => handleRemoveCriterion(criterionKey)}
  //                     className="btn-remove-criterion"
  //                     title="Remove criterion"
  //                   >
  //                     🗑️
  //                   </button>
  //                 </div>
                  
  //                 {/* Percentage Slider */}
  //                 <div className="criterion-field">
  //                   <label>Weight/Percentage:</label>
  //                   <div className="percentage-slider-container">
  //                     <input 
  //                       type="range" 
  //                       min="0" 
  //                       max="100" 
  //                       value={criterion.percentage || 0}
  //                       onChange={(e) => handleCriterionPercentageChange(criterionKey, e.target.value)}
  //                       className="percentage-slider"
  //                     />
  //                     <input
  //                       type="number"
  //                       min="0"
  //                       max="100"
  //                       value={criterion.percentage || 0}
  //                       onChange={(e) => handleCriterionPercentageChange(criterionKey, e.target.value)}
  //                       className="percentage-input"
  //                     />
  //                     <span className="percentage-label">%</span>
  //                     <span className="points-display">({criterion.max || 0} points)</span>
  //                   </div>
  //                 </div>
                  
  //                 {/* Description */}
  //                 <div className="criterion-field">
  //                   <label>📋 Description:</label>
  //                   <textarea 
  //                     value={criterion.description || ''}
  //                     onChange={(e) => handleCriterionFieldChange(criterionKey, 'description', e.target.value)}
  //                     className="edit-textarea"
  //                     rows="2"
  //                     placeholder="What does this criterion evaluate?"
  //                   />
  //                 </div>
                  
  //                 {/* Full Credit Criteria */}
  //                 <div className="criterion-field">
  //                   <label>Full Credit Criteria:</label>
  //                   <textarea 
  //                     value={criterion.fullCredit || ''}
  //                     onChange={(e) => handleCriterionFieldChange(criterionKey, 'fullCredit', e.target.value)}
  //                     className="edit-textarea"
  //                     rows="2"
  //                     placeholder="What earns full points?"
  //                   />
  //                 </div>
                  
  //                 {/* Partial Credit High */}
  //                 <div className="criterion-field">
  //                   <label>Partial Credit High (70-85%):</label>
  //                   <textarea 
  //                     value={criterion.partialCreditHigh || ''}
  //                     onChange={(e) => handleCriterionFieldChange(criterionKey, 'partialCreditHigh', e.target.value)}
  //                     className="edit-textarea"
  //                     rows="2"
  //                     placeholder="What earns 70-85% of this criterion's points?"
  //                   />
  //                 </div>

  //                 {/* Partial Credit Low */}
  //                 <div className="criterion-field">
  //                   <label>Partial Credit Low (40-65%):</label>
  //                   <textarea 
  //                     value={criterion.partialCreditLow || ''}
  //                     onChange={(e) => handleCriterionFieldChange(criterionKey, 'partialCreditLow', e.target.value)}
  //                     className="edit-textarea"
  //                     rows="2"
  //                     placeholder="What earns 40-65% of this criterion's points?"
  //                   />
  //                 </div>
                  
  //                 {/* No Credit Criteria */}
  //                 <div className="criterion-field">
  //                   <label>No Credit Criteria:</label>
  //                   <textarea 
  //                     value={criterion.noCredit || ''}
  //                     onChange={(e) => handleCriterionFieldChange(criterionKey, 'noCredit', e.target.value)}
  //                     className="edit-textarea"
  //                     rows="2"
  //                     placeholder="What earns no points?"
  //                   />
  //                 </div>
                  
  //               </div>
  //             ))}
  //           </div>
            
  //           {/* Add Criterion Button */}
  //           <button
  //             type="button"
  //             onClick={handleAddCriterion}
  //             className="btn-add-criterion"
  //           >
  //             ➕ Add Criterion
  //           </button>
            
  //           {/* Validation Status */}
  //           <div className={`rubric-validation ${totalPercentage === 100 ? 'valid' : 'invalid'}`}>
  //             <span>
  //               Total: {totalPercentage}% 
  //               {totalPercentage === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}
  //             </span>
  //           </div>
            
  //           {/* Action Buttons */}
  //           <div className="rubric-editor-actions">
  //             <button
  //               type="button"
  //               onClick={handleResetToOriginal}
  //               className="btn-reset-rubric"
  //             >
  //               🔄 Reset to Original
  //             </button>
  //             <button
  //               type="button"
  //               onClick={handleAcceptRubric}
  //               disabled={totalPercentage !== 100}
  //               className="btn-accept-rubric"
  //             >
  //               ✓ Accept & Apply Rubric
  //             </button>
  //             <button
  //               type="button"
  //               onClick={handleRejectRubric}
  //               className="btn-reject-rubric"
  //             >
  //               ✕
  //             </button>
  //           </div>
  //         </div>
  //       )}
        
  //       <LabGradingRubric
  //         iotRequirements={question.iotRequirements}
  //         questionId={question.questionId}
  //         isExpanded={expandedRubrics[question.questionId]}
  //         onToggle={toggleRubric}
  //       />
      
  //       {/* Show if rubric already applied to question */}
  //       {/* {question.aiGradeMetadata && (!isEditingRubric || !isCurrentQuestion) && (
  //         <div className="applied-rubric-indicator">
  //           <span className="rubric-badge">✓ AI Rubric Applied</span>
  //           <button
  //             type="button"
  //             onClick={() => handleGenerateRubric(itemIdx, qIdx)}
  //             className="btn-regenerate"
  //           >
  //             🔄 Regenerate
  //           </button>
  //         </div>
  //       )} */}

  //     </div>
  //   );
  // };


  // const handleEditRubric = (question, itemIdx, qIdx) => {
  //   setRubricQuestionIndex({ itemIdx, questionIdx: qIdx });
  //   setEditableRubric(JSON.parse(JSON.stringify(question.aiGradeMetadata)));
  //   setIsEditingRubric(true);
  //   setTimeout(() => {
  //     document.querySelector('.rubric-editor-box')?.scrollIntoView({ behavior: 'smooth' });
  //   }, 100);
  // };

  // const renderRubricView = (question, itemIdx, qIdx) => {
  //   if (!['essay', 'code', 'short_answer', 'lab_practical'].includes(question.questionType)) {
  //     return null;
  //   }

  //   const isCurrentQuestion = rubricQuestionIndex?.itemIdx === itemIdx && rubricQuestionIndex?.questionIdx === qIdx;
  //   const totalPercentage = isCurrentQuestion && editableRubric ? calculateTotalPercentage() : 0;

  //   return (
  //     <>
  //       {question.explanation && (
  //         <div className="question-explanation">
  //           <strong>💡 Explanation:</strong>
  //           <p>{question.explanation}</p>
  //         </div>
  //       )}

  //       {question.aiGradeMetadata && (
  //         <div className="saved-rubric-display">
  //             <div 
  //                 className="rubric-display-header rubric-header-clickable"
  //                 onClick={() => toggleRubric(question.questionId)}
  //                 style={{
  //                     cursor: 'pointer',
  //                     display: 'flex',
  //                     justifyContent: 'space-between',
  //                     alignItems: 'center',
  //                     padding: '12px 16px',
  //                     background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  //                     borderRadius: '8px',
  //                     marginBottom: expandedRubrics[question.questionId] ? '12px' : '0',
  //                     transition: 'all 0.3s ease'
  //                 }}
  //             >
  //                 <strong style={{ color: 'white', margin: 0 }}>
  //                     🤖 Prof-iX Grading Rubric
  //                 </strong>
  //                 <span style={{ 
  //                     color: 'white', 
  //                     fontSize: '20px',
  //                     transition: 'transform 0.3s ease',
  //                     transform: expandedRubrics[question.questionId] ? 'rotate(180deg)' : 'rotate(0deg)',
  //                     display: 'inline-block'
  //                 }}>
  //                     ▼
  //                 </span>
  //             </div>
              
  //             {expandedRubrics[question.questionId] && (
  //                 <div style={{ animation: 'slideDown 0.3s ease-out' }}>
  //                     <div className="rubric-summary-text">
  //                         <strong>SUMMARY:</strong>
  //                         <p>{question.aiGradeMetadata.rubricText}</p>
  //                     </div>
                      
  //                     <div className="rubric-criteria-display">
  //                         <strong>Grading Criteria:</strong>
  //                         {Object.entries(question.aiGradeMetadata.rubric).map(([criterionKey, criterion]) => (
  //                             <div key={criterionKey} className="criterion-display-card">
  //                                 <div className="criterion-display-header">
  //                                     <span className="criterion-display-name">
  //                                         {criterionKey.replace(/_/g, ' ').toUpperCase()}
  //                                     </span>
  //                                     <span className="criterion-display-points">
  //                                         {criterion.max} pts ({((criterion.max / question.points) * 100).toFixed(0)}%)
  //                                     </span>
  //                                 </div>
  //                                 <div className="criterion-display-content">
  //                                     <p><strong>📋 Description:</strong> {criterion.description}</p>
  //                                     <p><strong><span style={{ color: '#10b981' }}>✓</span> Full Credit:</strong> {criterion.fullCredit}</p>
  //                                     <p><strong><span style={{ color: '#f59e0b' }}>◐</span> Partial Credit High:</strong> {criterion.partialCreditHigh}</p>
  //                                     <p><strong><span style={{ color: '#f59e0b' }}>◑</span> Partial Credit Low:</strong> {criterion.partialCreditLow}</p>
  //                                     <p><strong><span style={{ color: '#ef4444' }}>✗</span> No Credit:</strong> {criterion.noCredit}</p>
  //                                 </div>
  //                             </div>
  //                         ))}
  //                     </div>
                      
  //                     <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
  //                       <button
  //                         type="button"
  //                         className="btn-generate-rubric"
  //                         onClick={() => handleEditRubric(question, itemIdx, qIdx)}
  //                       >
  //                         ✏️ Edit Rubric
  //                       </button>
  //                     </div>

  //                 </div>
  //             )}
  //         </div>
  //       )}


  //     </>
  //   );
  // };

  // const renderIoTRequirementsBuilder = (question, idx, qIdx) => {

  //   const handleAIGenerate = async () => {
  //     if (!question.questionText?.trim() || !question.labRequirements?.trim()) {
  //       showNotification('Please fill in Lab Objective and Lab Requirements first', 'error');
  //       return;
  //     }
  //     setIsGeneratingRequirements(true);
  //     setGenerationRequirementError(null);
  //     try {
  //       const response = await generateCompleteLabRequirements(
  //         question.questionText,
  //         question.labRequirements,
  //         question.points || 0
  //       );
  //       if (response.success) {
  //         setGeneratedPreview(response.data);
  //         showNotification(`${response.data.length} requirements generated! Review and edit before accepting.`, 'success');
  //       } else {
  //         setGenerationRequirementError(response.error || 'Failed to generate requirements');
  //         showNotification(response.error || 'Failed to generate requirements', 'error');
  //       }
  //     } catch (error) {
  //       setGenerationRequirementError(error.message);
  //       showNotification('AI generation failed', 'error');
  //     } finally {
  //       setIsGeneratingRequirements(false);
  //     }
  //   };

  //   const handleAcceptAI = () => {
  //     if (!generatedPreview || !Array.isArray(generatedPreview)) return;
  //     const newRequirements = generatedPreview.map((req, i) => ({
  //       ...req,
  //       id: `R${i + 1}`
  //     }));
  //     const updated = newRequirements;
  //     const totalPoints = updated.reduce((sum, r) => sum + (r.points || 0), 0);
  //     handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //     handleQuestionChange(idx, qIdx, 'points', totalPoints);
  //     setGeneratedPreview(null);
  //     setGenerationRequirementError(null);
  //     showNotification(`${newRequirements.length} requirements added! Total: ${totalPoints} points.`, 'success');
  //   };

  //   const handleRejectAI = () => {
  //     setGeneratedPreview(null);
  //     setGenerationRequirementError(null);
  //   };

  //   // Update preview field
  //   const handlePreviewFieldChange = (previewIdx, field, value) => {
  //     setGeneratedPreview(prev => {
  //       const updated = [...prev];
  //       updated[previewIdx] = { ...updated[previewIdx], [field]: value };
  //       // Recalculate points from percentage when percentage changes
  //       if (field === 'percentage') {
  //         const totalPts = question.points || 0;
  //         updated[previewIdx].points = Math.round((value / 100) * totalPts * 100) / 100;
  //       }
  //       return updated;
  //     });
  //   };

  //   // Update preview rubric field
  //   const handlePreviewRubricChange = (previewIdx, field, value) => {
  //     setGeneratedPreview(prev => {
  //       const updated = [...prev];
  //       updated[previewIdx] = {
  //         ...updated[previewIdx],
  //         rubric: { ...updated[previewIdx].rubric, [field]: value }
  //       };
  //       return updated;
  //     });
  //   };

  //   // Update preview partial tier
  //   const handlePreviewTierChange = (previewIdx, tierIdx, field, value) => {
  //     setGeneratedPreview(prev => {
  //       const updated = [...prev];
  //       const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
  //       newPartial[tierIdx] = {
  //         ...newPartial[tierIdx],
  //         [field]: field === 'threshold' || field === 'pointsPercentage' ? Number(value) : value,
  //         ...(field === 'threshold' ? { pointsPercentage: Number(value) / 100 } : {})
  //       };
  //       updated[previewIdx] = {
  //         ...updated[previewIdx],
  //         rubric: { ...updated[previewIdx].rubric, partial: newPartial }
  //       };
  //       return updated;
  //     });
  //   };

  //   const handlePreviewRemoveTier = (previewIdx, tierIdx) => {
  //     setGeneratedPreview(prev => {
  //       const updated = [...prev];
  //       const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
  //       newPartial.splice(tierIdx, 1);
  //       updated[previewIdx] = {
  //         ...updated[previewIdx],
  //         rubric: { ...updated[previewIdx].rubric, partial: newPartial }
  //       };
  //       return updated;
  //     });
  //   };

  //   const handlePreviewAddTier = (previewIdx) => {
  //     setGeneratedPreview(prev => {
  //       const updated = [...prev];
  //       const newPartial = [...(updated[previewIdx].rubric?.partial || [])];
  //       newPartial.push({ threshold: 70, description: '', pointsPercentage: 0.7 });
  //       updated[previewIdx] = {
  //         ...updated[previewIdx],
  //         rubric: { ...updated[previewIdx].rubric, partial: newPartial }
  //       };
  //       return updated;
  //     });
  //   };

  //   // Existing requirement helpers
  //   const handleRequirementFieldChange = (reqIdx, field, value) => {
  //     const updated = [...(question.iotRequirements || [])];
  //     updated[reqIdx] = { ...updated[reqIdx], [field]: value };
  //     // Recalculate points from percentage
  //     if (field === 'percentage') {
  //       updated[reqIdx].points = Math.round((value / 100) * question.points * 100) / 100;
  //     }
  //     handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //     // Recalculate total points if points changed
  //     if (field === 'points') {
  //       const totalPoints = updated.reduce((sum, r) => sum + (parseFloat(r.points) || 0), 0);
  //       handleQuestionChange(idx, qIdx, 'points', totalPoints);
  //     }
  //   };

  //   const handleRubricFieldChange = (reqIdx, field, value) => {
  //     const updated = [...(question.iotRequirements || [])];
  //     updated[reqIdx] = {
  //       ...updated[reqIdx],
  //       rubric: { ...updated[reqIdx].rubric, [field]: value }
  //     };
  //     handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //   };

  //   const handleRubricPartialChange = (reqIdx, tierIdx, field, value) => {
  //     const updated = [...(question.iotRequirements || [])];
  //     const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
  //     newPartial[tierIdx] = {
  //       ...newPartial[tierIdx],
  //       [field]: field === 'threshold' || field === 'pointsPercentage' ? Number(value) : value,
  //       ...(field === 'threshold' ? { pointsPercentage: Number(value) / 100 } : {})
  //     };
  //     updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
  //     handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //   };

  //   const previewTotalPercentage = generatedPreview?.reduce((sum, r) => sum + (r.percentage || 0), 0) || 0;

  //   return (
  //     <div className="edit-field iot-requirements-builder">
  //       <label>🔡 IoT Requirements / Grading Criteria:</label>
  //       <p className="field-hint">Define requirements for AI grading of IoT lab sessions</p>

  //       {/* ============================================================ */}
  //       {/* AI ASSISTANT SECTION                                          */}
  //       {/* ============================================================ */}
  //       <div className="ai-assistant-section">
  //         <div className="ai-assistant-header">
  //           <h6>✨ AI Assistant - Generate Requirements & Rubrics</h6>
  //           <span className="ai-badge">Powered by OpenAI</span>
  //         </div>

  //         <div className="ai-assistant-form">
  //           <div className="ai-form-column">

  //             {/* Lab Objective — read only from questionText */}
  //             <div className="ai-form-input-field">
  //               <label>Lab Objective (from question above):</label>
  //               <textarea
  //                 value={question.questionText || ''}
  //                 className="ai-input"
  //                 rows={2}
  //                 readOnly
  //                 style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
  //               />
  //             </div>

  //             {/* Lab Requirements — read only from question.labRequirements */}
  //             <div className="ai-form-input-field">
  //               <label>Lab Requirements (from field above):</label>
  //               <textarea
  //                 value={question.labRequirements || ''}
  //                 className="ai-input"
  //                 rows={4}
  //                 readOnly
  //                 style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
  //               />
  //             </div>

  //             {/* Total Points — read only from question.points */}
  //             <div className="ai-form-field" style={{ width: '200px' }}>
  //               <label>Total Points (from question above):</label>
  //               <input
  //                 type="number"
  //                 value={question.points || 0}
  //                 className="ai-input"
  //                 readOnly
  //                 style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed', color: '#6b7280' }}
  //               />
  //             </div>
  //           </div>

  //           <button
  //             type="button"
  //             onClick={handleAIGenerate}
  //             className="btn-ai-generate"
  //             disabled={
  //               isGeneratingRequirements ||
  //               !!generatedPreview ||
  //               !question.questionText?.trim() ||
  //               !question.labRequirements?.trim() ||
  //               !question.points
  //             }
  //           >
  //             {isGeneratingRequirements
  //               ? <><span className="ai-spinner">⏳</span> Generating...</>
  //               : (question.iotRequirements?.length > 0
  //                   ? <><span className="ai-icon">🔄</span> Regenerate All Requirements with AI</>
  //                   : <><span className="ai-icon">✨</span> Generate All Requirements with AI</>
  //                 )
  //             }
  //           </button>

  //           {!question.points && (
  //             <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px' }}>
  //               ⚠️ Set Total Points on the question before generating
  //             </p>
  //           )}
  //         </div>

  //         {/* Error Display */}
  //         {generationRequirementError && (
  //           <div className="ai-error-box">
  //             <span className="error-icon">⚠️</span>
  //             <span>{generationRequirementError}</span>
  //           </div>
  //         )}

  //         {/* ============================================================ */}
  //         {/* AI PREVIEW — Fully Editable with Percentage Sliders           */}
  //         {/* ============================================================ */}
  //         {generatedPreview && Array.isArray(generatedPreview) && (
  //           <div className="rubric-editor-box">
  //             <div className="rubric-editor-header">
  //               <h5>📝 Edit AI-Generated Requirements</h5>
  //               <span className="edit-hint">Adjust percentages and rubric text before accepting</span>
  //             </div>

  //             {/* Total percentage validation */}
  //             <div className={`rubric-validation ${previewTotalPercentage === 100 ? 'valid' : 'invalid'}`}>
  //               <span>
  //                 Total: {previewTotalPercentage}%
  //                 {previewTotalPercentage === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}
  //               </span>
  //             </div>

  //             {generatedPreview.map((req, previewIdx) => (
  //               <div key={previewIdx} className="criterion-editor-card">

  //                 {/* Requirement header */}
  //                 <div className="criterion-header">
  //                   <span className="criterion-name-input" style={{ fontWeight: 600 }}>
  //                     Requirement {req.requirementIndex}
  //                   </span>
  //                 </div>

  //                 {/* Description */}
  //                 <div className="criterion-field">
  //                   <label>📋 Description:</label>
  //                   <textarea
  //                     value={req.description || ''}
  //                     onChange={(e) => handlePreviewFieldChange(previewIdx, 'description', e.target.value)}
  //                     className="edit-textarea"
  //                     rows={2}
  //                   />
  //                 </div>

  //                 {/* Percentage Slider */}
  //                 <div className="criterion-field">
  //                   <label>Weight / Percentage:</label>
  //                   <div className="percentage-slider-container">
  //                     <input
  //                       type="range"
  //                       min="0"
  //                       max="100"
  //                       value={req.percentage || 0}
  //                       onChange={(e) => handlePreviewFieldChange(previewIdx, 'percentage', Number(e.target.value))}
  //                       className="percentage-slider"
  //                     />
  //                     <input
  //                       type="number"
  //                       min="0"
  //                       max="100"
  //                       value={req.percentage || 0}
  //                       onChange={(e) => handlePreviewFieldChange(previewIdx, 'percentage', Number(e.target.value))}
  //                       className="percentage-input"
  //                     />
  //                     <span className="percentage-label">%</span>
  //                     <span className="points-display">({req.points || 0} points)</span>
  //                   </div>
  //                 </div>

  //                 {/* Full Credit */}
  //                 <div className="criterion-field">
  //                   <label>✓ Full Credit:</label>
  //                   <textarea
  //                     value={req.rubric?.full || ''}
  //                     onChange={(e) => handlePreviewRubricChange(previewIdx, 'full', e.target.value)}
  //                     className="edit-textarea"
  //                     rows={2}
  //                   />
  //                 </div>

  //                 {/* Partial Tiers */}
  //                 <div className="criterion-field">
  //                   <label>◐ Partial Credit Tiers: 
  //                     <span style={{fontSize:'11px', color:'#6b7280'}}>
  //                       (each tier = minimum % of work needed to earn that credit level — student matches highest tier they qualify for)
  //                     </span>
  //                   </label>
  //                   {(req.rubric?.partial || []).map((tier, tIdx) => (
  //                     <div key={tIdx} className="rubric-tier-edit-row">
  //                       <input
  //                         type="number"
  //                         value={tier.threshold || 0}
  //                         onChange={(e) => handlePreviewTierChange(previewIdx, tIdx, 'threshold', e.target.value)}
  //                         placeholder="Threshold %"
  //                         className="edit-input"
  //                         style={{ width: '90px' }}
  //                         min="0" max="100"
  //                       />
  //                       <input
  //                         type="number"
  //                         value={tier.pointsPercentage || 0}
  //                         onChange={(e) => handlePreviewTierChange(previewIdx, tIdx, 'pointsPercentage', e.target.value)}
  //                         placeholder="Points %"
  //                         className="edit-input"
  //                         style={{ width: '90px' }}
  //                         min="0" max="1" step="0.05"
  //                       />
  //                       <textarea
  //                         value={tier.description || ''}
  //                         onChange={(e) => handlePreviewTierChange(previewIdx, tIdx, 'description', e.target.value)}
  //                         placeholder="Description"
  //                         className="edit-textarea"
  //                         rows={2}
  //                         style={{ flex: 1 }}
  //                       />
  //                       <button
  //                         type="button"
  //                         onClick={() => handlePreviewRemoveTier(previewIdx, tIdx)}
  //                         className="btn-remove-tier"
  //                       >✕</button>
  //                     </div>
  //                   ))}
  //                   <button
  //                     type="button"
  //                     onClick={() => handlePreviewAddTier(previewIdx)}
  //                     className="btn-add-tier"
  //                   >➕ Add Tier</button>
  //                 </div>

  //                 {/* Fail */}
  //                 <div className="criterion-field">
  //                   <label>✗ Fail:</label>
  //                   <textarea
  //                     value={req.rubric?.fail || ''}
  //                     onChange={(e) => handlePreviewRubricChange(previewIdx, 'fail', e.target.value)}
  //                     className="edit-textarea"
  //                     rows={2}
  //                   />
  //                 </div>

  //               </div>
  //             ))}

  //             {/* Action Buttons */}
  //             <div className="rubric-editor-actions">
  //               <button
  //                 type="button"
  //                 onClick={handleAcceptAI}
  //                 disabled={previewTotalPercentage !== 100}
  //                 className="btn-accept-rubric"
  //               >
  //                 ✓ Accept & Add All Requirements
  //               </button>
  //               <button
  //                 type="button"
  //                 onClick={handleRejectAI}
  //                 className="btn-reject-rubric"
  //               >
  //                 ✕
  //               </button>
  //             </div>
  //           </div>
  //         )}
  //       </div>

  //       {/* ============================================================ */}
  //       {/* EXISTING REQUIREMENTS — Editable with Edit Button             */}
  //       {/* ============================================================ */}
  //       {(question.iotRequirements || []).length > 0 && (
  //         <div className="existing-requirements-section">
  //           <div className="existing-requirements-header">
  //             {(() => {
  //               const total = (question.iotRequirements || []).reduce((sum, r) => sum + (r.percentage || 0), 0);
  //               return (
  //                 <div className={`rubric-validation ${total === 100 ? 'valid' : 'invalid'}`}
  //                   style={{ marginBottom: '12px' }}>
  //                   <span>
  //                     Requirements total: {total}%
  //                     {total === 100 ? ' ✓' : ' ⚠️ Must equal 100%'}
  //                   </span>
  //                 </div>
  //               );
  //             })()}
  //             <h6>📋 Requirements ({question.iotRequirements.length})</h6>
  //             <button
  //               type="button"
  //               onClick={() => setEditingIoTRequirements(prev => ({
  //                 ...prev,
  //                 [question.questionId]: !prev[question.questionId]
  //               }))}
  //               className="btn-generate-rubric"
  //             >
  //               {editingIoTRequirements[question.questionId] ? '🔒 Done Editing' : '✏️ Edit Requirements'}
  //             </button>
  //           </div>

  //           {(question.iotRequirements || []).map((req, reqIdx) => (
  //             <div key={reqIdx} className="requirement-card">

  //               <div className="requirement-card-header">
  //                 <h5>Requirement {reqIdx + 1}</h5>
  //                 {editingIoTRequirements[question.questionId] && (
  //                   <button
  //                     type="button"
  //                     className="btn-remove-requirement"
  //                     onClick={() => {
  //                       const updated = [...(question.iotRequirements || [])];
  //                       updated.splice(reqIdx, 1);
  //                       handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //                       const totalPoints = updated.reduce((sum, r) => sum + (r.points || 0), 0);
  //                       handleQuestionChange(idx, qIdx, 'points', totalPoints);
  //                     }}
  //                   >
  //                     🗑️ Remove
  //                   </button>
  //                 )}
  //               </div>

  //               {editingIoTRequirements[question.questionId] ? (
  //                 // EDIT MODE
  //                 <>
  //                   <div className="edit-field">
  //                     <label>📋 Description:</label>
  //                     <textarea
  //                       value={req.description || ''}
  //                       onChange={(e) => handleRequirementFieldChange(reqIdx, 'description', e.target.value)}
  //                       className="edit-textarea"
  //                       rows={2}
  //                     />
  //                   </div>

  //                   {/* Percentage Slider */}
  //                   <div className="criterion-field">
  //                     <label>Weight / Percentage:</label>
  //                     <div className="percentage-slider-container">
  //                       <input
  //                         type="range"
  //                         min="0"
  //                         max="100"
  //                         value={req.percentage || 0}
  //                         onChange={(e) => handleRequirementFieldChange(reqIdx, 'percentage', Number(e.target.value))}
  //                         className="percentage-slider"
  //                       />
  //                       <input
  //                         type="number"
  //                         min="0"
  //                         max="100"
  //                         value={req.percentage || 0}
  //                         onChange={(e) => handleRequirementFieldChange(reqIdx, 'percentage', Number(e.target.value))}
  //                         className="percentage-input"
  //                       />
  //                       <span className="percentage-label">%</span>
  //                       <span className="points-display">({req.points || 0} points)</span>
  //                     </div>
  //                   </div>

  //                   {/* Rubric */}
  //                   <div className="edit-field">
  //                     <h6>📏 Rubric</h6>
  //                     <div className="edit-field">
  //                       <label>✓ Full Credit:</label>
  //                       <textarea
  //                         value={req.rubric?.full || ''}
  //                         onChange={(e) => handleRubricFieldChange(reqIdx, 'full', e.target.value)}
  //                         className="edit-textarea"
  //                         rows={2}
  //                       />
  //                     </div>

  //                     <div className="edit-field">
  //                       <label>◐ Partial Tiers:
  //                         <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '8px' }}>
  //                           (student matches highest tier they qualify for)
  //                         </span>
  //                       </label>

  //                       {(req.rubric?.partial || []).map((tier, tierIdx) => (
  //                         <div key={tierIdx} className="rubric-tier-edit-row" style={{ flexDirection: 'column', gap: '8px', padding: '12px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px' }}>
                            
  //                           <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  //                             <span style={{ fontWeight: 600, fontSize: '13px' }}>Tier {tierIdx + 1}</span>
  //                             <button
  //                               type="button"
  //                               onClick={() => {
  //                                 const updated = [...(question.iotRequirements || [])];
  //                                 const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
  //                                 newPartial.splice(tierIdx, 1);
  //                                 updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
  //                                 handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //                               }}
  //                               className="btn-remove-tier"
  //                             >✕ Remove</button>
  //                           </div>

  //                           {/* Threshold slider */}
  //                           <div className="criterion-field">
  //                             <label style={{ fontSize: '12px' }}>
  //                               Minimum work needed to reach this tier:
  //                               <strong style={{ marginLeft: '6px' }}>{tier.threshold || 0}%</strong>
  //                             </label>
  //                             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  //                               <input
  //                                 type="range"
  //                                 min="0" max="100"
  //                                 value={tier.threshold || 0}
  //                                 onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'threshold', Number(e.target.value))}
  //                                 className="percentage-slider"
  //                                 style={{ flex: 1 }}
  //                               />
  //                               <input
  //                                 type="number"
  //                                 value={tier.threshold || 0}
  //                                 onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'threshold', Number(e.target.value))}
  //                                 className="edit-input"
  //                                 style={{ width: '70px' }}
  //                                 min="0" max="100"
  //                               />
  //                               <span style={{ fontSize: '12px' }}>%</span>
  //                             </div>
  //                           </div>

  //                           {/* Points percentage slider */}
  //                           <div className="criterion-field">
  //                             <span style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
  //                               Points earned at this tier: {Math.round((tier.threshold || 0) / 100 * (req.points || 0) * 100) / 100} pts
  //                             </span>
  //                             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
  //                               <input
  //                                 type="range"
  //                                 min="0" max="100"
  //                                 value={Math.round((tier.pointsPercentage || 0) * 100)}
  //                                 onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'pointsPercentage', Number(e.target.value) / 100)}
  //                                 className="percentage-slider"
  //                                 style={{ flex: 1 }}
  //                               />
  //                               <input
  //                                 type="number"
  //                                 value={Math.round((tier.pointsPercentage || 0) * 100)}
  //                                 onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'pointsPercentage', Number(e.target.value) / 100)}
  //                                 className="edit-input"
  //                                 style={{ width: '70px' }}
  //                                 min="0" max="100"
  //                               />
  //                               <span style={{ fontSize: '12px' }}>%</span>
  //                             </div>
  //                           </div>

  //                           {/* Description */}
  //                           <div className="criterion-field">
  //                             <label style={{ fontSize: '12px' }}>📋 Description:</label>
  //                             <textarea
  //                               value={tier.description || ''}
  //                               onChange={(e) => handleRubricPartialChange(reqIdx, tierIdx, 'description', e.target.value)}
  //                               placeholder="Describe what earns this tier..."
  //                               className="edit-textarea"
  //                               rows={2}
  //                             />
  //                           </div>

  //                         </div>
  //                       ))}

  //                       {/* Descending order warning */}
  //                       {(req.rubric?.partial || []).length > 1 &&
  //                         !([...(req.rubric?.partial || [])].every((t, i, arr) =>
  //                           i === 0 || arr[i - 1].threshold > t.threshold
  //                         )) && (
  //                         <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
  //                           ⚠️ Thresholds must be in descending order (highest first)
  //                         </p>
  //                       )}

  //                       <button
  //                         type="button"
  //                         onClick={() => {
  //                           const updated = [...(question.iotRequirements || [])];
  //                           const newPartial = [...(updated[reqIdx].rubric?.partial || [])];
  //                           newPartial.push({ threshold: 70, description: '', pointsPercentage: 0.7 });
  //                           updated[reqIdx] = { ...updated[reqIdx], rubric: { ...updated[reqIdx].rubric, partial: newPartial } };
  //                           handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //                         }}
  //                         className="btn-add-tier"
  //                       >➕ Add Tier</button>
  //                     </div>

  //                     <div className="edit-field">
  //                       <label>✗ Fail:</label>
  //                       <textarea
  //                         value={req.rubric?.fail || ''}
  //                         onChange={(e) => handleRubricFieldChange(reqIdx, 'fail', e.target.value)}
  //                         className="edit-textarea"
  //                         rows={2}
  //                       />
  //                     </div>
  //                   </div>
  //                 </>
  //               ) : (
  //                 // VIEW MODE
  //                 <div className="criterion-display-card">
  //                   <div className="criterion-display-header">
  //                     <span className="criterion-display-name">{req.description}</span>
  //                     <span className="criterion-display-points">
  //                       {req.points} pts ({req.percentage}%)
  //                     </span>
  //                   </div>
  //                   <div className="criterion-display-content">
  //                     <p><strong><span style={{ color: '#10b981' }}>✓</span> Full Credit:</strong> {req.rubric?.full}</p>
  //                     {(req.rubric?.partial || []).map((tier, tIdx) => (
  //                       <p key={tIdx}>
  //                         <strong><span style={{ color: '#f59e0b' }}>◐</span> {tier.threshold}%:</strong> {tier.description}
  //                       </p>
  //                     ))}
  //                     <p><strong><span style={{ color: '#ef4444' }}>✗</span> Fail:</strong> {req.rubric?.fail}</p>
  //                   </div>
  //                 </div>
  //               )}

  //             </div>
  //           ))}
  //         </div>
  //       )}

  //       {/* Manual Add Button */}
  //       <button
  //         type="button"
  //         className="btn-add-requirement"
  //         onClick={() => {
  //           const newReq = {
  //             id: `R${(question.iotRequirements?.length || 0) + 1}`,
  //             description: '',
  //             percentage: 0,
  //             points: 0,
  //             rubric: { full: '', partial: [], fail: '' }
  //           };
  //           const updated = [...(question.iotRequirements || []), newReq];
  //           handleQuestionChange(idx, qIdx, 'iotRequirements', updated);
  //         }}
  //       >
  //         ➕ Add Requirement Manually
  //       </button>

  //     </div>
  //   );
  // };

  // const handleViewAnnouncement = (announcement) => {
  //   setSelectedAnnouncement(announcement);
  //   setAnnouncementModalVisible(true);
  // };

  const profileHeaderData = {
    ...authData,
    title: `📚 Course: ${courseInfo?.title} (${courseInfo?.courseId})`,
    // subTitle: labInfo?.title || courseInfo?.courseId || 'Circuit Design'
  };

  return (
    <div className="main-container">
      <div className="main-content">
        
        {/* Header */}
        <div className="header-row">
          <ProfileHeader 
              data = {profileHeaderData}
              activePage = "instructor/dashboard" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader 
            notificationCount={newMessagesCount + newAnnouncementsCount} 
            icons={['envelope']} 
          />
        </div>

        {/* Tab Navigation */}
        <div className="top-tab-container">
          <BackButton 
            to="/instructor/dashboard"
            activeTab="management"
            label="←"
          />
          {/* Contents Tab */}
          <button
            className={`tab-button ${activeTab === 'content' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            📊 Content
          </button>
          
          {/* Students Tab */}
          <button
            className={`tab-button ${activeTab === 'students' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('students')}
          >
            👥 Students' Progress ({courseInfo?.enrolledStudents})
          </button>
          
          {/* Grading Tab */}
          <button
            className={`tab-button ${activeTab === 'grading' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('grading')}
          >
            ✅ Grading ({statsAssignments.allPending} / {statsAssignments.allSubmitted})
          </button>
          
          {/* Messages Tab */}
          <button
            className={`tab-button ${activeTab === 'communication' ? 'active-tab' : ''}`}
            onClick={() => setActiveTab('communication')}
          >
            💬 Communication
            {unreadCount > 0 && (
              <span className="notification-badge"> ({unreadCount})</span>
            )}
          </button>



        </div>
        
        <div className="main-content-area">
          {/* Content Area */}
          {isLoading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading course data...</p>
            </div>
          ) : (
            <>      
              {/* Contents Tab */}
              {activeTab === 'content' && (
              <div style={{ width: '80%' }}>

                  {/* ── Content Navigation ── */}
                  <div className="acd-section" style={{ marginBottom: '16px' }}>
                  <div className="acd-section-title">Course Content</div>
                  <div className="acd-content-grid">
                      <button className="acd-content-btn" onClick={() => openListModal('lessons')}>
                      📚 <span>Lessons</span> <span className="acd-content-count">({courseInfo?.lessons?.length || 0})</span>
                      </button>
                      <button className="acd-content-btn" onClick={() => openListModal('labs')}>
                      🧪 <span>Labs</span> <span className="acd-content-count">({courseInfo?.labs?.length || 0})</span>
                      </button>
                      <button className="acd-content-btn" onClick={() => openListModal('quizzes')}>
                      📋 <span>Quizzes</span> <span className="acd-content-count">({courseInfo?.quizzes?.length || 0})</span>
                      </button>
                      <button className="acd-content-btn" onClick={() => openListModal('exams')}>
                      📄 <span>Exams</span> <span className="acd-content-count">({courseInfo?.exams?.length || 0})</span>
                      </button>
                      <button className="acd-content-btn" onClick={() => openListModal('capstones')}>
                      📘 <span>Capstones</span> <span className="acd-content-count">({courseInfo?.capstones?.length || 0})</span>
                      </button>
                      {courseInfo?.category === 'IoT' && (
                      <button className="acd-content-btn" onClick={() => navigate(`/iot/course/${courseInfo.courseId}/circuits`)}>
                          🔧 <span>My Circuits</span> <span className="acd-content-count">({courseCircuits?.length || 0})</span>
                      </button>
                      )}

                <button className="acd-content-btn" onClick={() => {
                  navigate(`/labengine/course/${courseInfo.courseId}/canvases`);
                }}>
                    {CourseDomains[courseInfo.category]?.buttonTitle || '🔧 My Circuits'}
                    <span className="acd-content-count">({myCircuitsCount})</span>
                </button>
                
                  </div>
                  </div>

                  {/* ── Back button ── */}
                  <div style={{ marginBottom: '16px' }}>
                  <button onClick={() => navigate('/student/dashboard')} className="acd-action-btn">
                      ← Back
                  </button>
                  </div>

                  {/* ── Course Overview ── */}
                  <div className="acd-section" style={{ marginBottom: '16px' }}>
                  <div className="acd-section-title-row">
                      <span className="acd-section-title">📖 Course Information</span>
                      {hasTextbook && (
                      <a
                          href={`/courses/${courseInfo.category}/index.html`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="acd-textbook-link"
                      >
                          <img src="/images/prof_ix.png" alt="Prof-iX" className="acd-textbook-img" />
                          📚 Course eBook
                      </a>
                      )}
                  </div>

                  <div className="acd-meta-grid">
                      <div className="acd-meta-item">
                      <img
                          src={courseInfo?.instructorImageUrl || '/images/default-avatar.png'}
                          alt={courseInfo?.instructorName}
                          className="acd-meta-avatar"
                      />
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Instructor</span>
                          <span className="acd-meta-value">{courseInfo?.instructorName}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">📧</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Instructor Email</span>
                          <span className="acd-meta-value">{courseInfo?.instructorEmail}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">📊</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Level</span>
                          <span className="acd-meta-value">{courseInfo?.level}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">⏱️</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Duration</span>
                          <span className="acd-meta-value">{courseInfo?.duration}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">🎓</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Credits</span>
                          <span className="acd-meta-value">{courseInfo?.credits}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">🎯</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Passing Grade</span>
                          <span className="acd-meta-value">{courseInfo?.passingGrade}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">📂</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Category</span>
                          <span className="acd-meta-value">{courseInfo?.category}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">👥</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Enrolled Students</span>
                          <span className="acd-meta-value">{courseInfo?.enrolledStudents}</span>
                      </div>
                      </div>
                      <div className="acd-meta-item">
                      <span className="acd-meta-icon">🔄</span>
                      <div className="acd-meta-content">
                          <span className="acd-meta-label">Last Updated</span>
                          <span className="acd-meta-value">{new Date(courseInfo?.updatedAt).toLocaleDateString()}</span>
                      </div>
                      </div>
                  </div>

                  <div className="acd-description">
                      <p className="acd-short-desc">{courseInfo?.shortDescription}</p>
                      <p className="acd-long-desc">{courseInfo?.longDescription}</p>
                  </div>
                  </div>

                  {/* ── Prerequisites ── */}
                  <div className="acd-section" style={{ marginBottom: '16px' }}>
                  <div className="acd-section-title">📱 Prerequisites</div>
                  <div className="acd-tags">
                      {courseInfo?.prerequisites?.length > 0
                      ? courseInfo.prerequisites.map((pre, idx) => (
                          <span key={idx} className="acd-tag acd-tag--prereq">{pre}</span>
                          ))
                      : <p className="acd-empty-msg">No prerequisites required</p>
                      }
                  </div>
                  </div>

                  {/* ── Tags ── */}
                  <div className="acd-section" style={{ marginBottom: '16px' }}>
                  <div className="acd-section-title">💡 Course Tags</div>
                  <div className="acd-tags">
                      {tags?.length > 0
                      ? tags.map((tag, idx) => (
                          <span key={idx} className="acd-tag acd-tag--course">{tag}</span>
                          ))
                      : <p className="acd-empty-msg">No tags available</p>
                      }
                  </div>
                  </div>

                  {/* ── Grading Scheme ── */}
                  <div className="acd-section">
                    <div className="acd-section-title">📊 Grading Scheme</div>

                    {courseInfo?.labs?.length > 0 && (
                      <div className="acd-grade-category">
                        <button className="acd-grade-category-hdr" onClick={() => toggleGrading('labs')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                          🧪 Labs
                          <span className="acd-grade-total">{Math.round(courseInfo.labs.reduce((s, l) => s + (l.gradeWeight || 0), 0) * 1000) / 10}%</span>
                          <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['labs'] ? '▼' : '▲'}</span>
                        </button>
                        {!gradingCollapsed['labs'] && courseInfo.labs.map((lab, idx) => (
                          <div key={idx} className="acd-weight-row">
                            <div className="acd-weight-info">
                              <span className="acd-weight-id">{lab.labId}</span>
                              <span className="acd-weight-title">{lab.title}</span>
                            </div>
                            {editingWeight?.type === 'lab' && editingWeight?.id === lab.labId ? (
                              <div className="acd-weight-edit">
                                <input type="number" min="0" max="100" step="0.1" autoFocus
                                  className="acd-weight-input" value={tempWeight}
                                  onChange={e => onTempWeightChange(e.target.value)} />
                                <span className="acd-weight-pct">%</span>
                                <button className="acd-wbtn acd-wbtn--save"   onClick={onWeightSave}>✓</button>
                                <button className="acd-wbtn acd-wbtn--cancel" onClick={onWeightCancel}>✕</button>
                              </div>
                            ) : (
                              <div className="acd-weight-display">
                                <span className="acd-weight-val">{Math.round((lab.gradeWeight || 0) * 1000) / 10}%</span>
                                <button className="acd-wbtn acd-wbtn--edit" onClick={() => onWeightEdit('lab', lab.labId, lab.gradeWeight)}>✏️</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {courseInfo?.quizzes?.length > 0 && (
                      <div className="acd-grade-category">
                        <button className="acd-grade-category-hdr" onClick={() => toggleGrading('quizzes')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                          📋 Quizzes
                          <span className="acd-grade-total">{Math.round(courseInfo.quizzes.reduce((s, q) => s + (q.gradeWeight || 0), 0) * 1000) / 10}%</span>
                          <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['quizzes'] ? '▼' : '▲'}</span>
                        </button>
                        {!gradingCollapsed['quizzes'] && courseInfo.quizzes.map((quiz, idx) => (
                          <div key={idx} className="acd-weight-row">
                            <div className="acd-weight-info">
                              <span className="acd-weight-id">{quiz.quizId}</span>
                              <span className="acd-weight-title">{quiz.title}</span>
                            </div>
                            {editingWeight?.type === 'quiz' && editingWeight?.id === quiz.quizId ? (
                              <div className="acd-weight-edit">
                                <input type="number" min="0" max="100" step="0.1" autoFocus
                                  className="acd-weight-input" value={tempWeight}
                                  onChange={e => onTempWeightChange(e.target.value)} />
                                <span className="acd-weight-pct">%</span>
                                <button className="acd-wbtn acd-wbtn--save"   onClick={onWeightSave}>✓</button>
                                <button className="acd-wbtn acd-wbtn--cancel" onClick={onWeightCancel}>✕</button>
                              </div>
                            ) : (
                              <div className="acd-weight-display">
                                <span className="acd-weight-val">{Math.round((quiz.gradeWeight || 0) * 1000) / 10}%</span>
                                <button className="acd-wbtn acd-wbtn--edit" onClick={() => onWeightEdit('quiz', quiz.quizId, quiz.gradeWeight)}>✏️</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {courseInfo?.exams?.length > 0 && (
                      <div className="acd-grade-category">
                        <button className="acd-grade-category-hdr" onClick={() => toggleGrading('exams')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                          📄 Exams
                          <span className="acd-grade-total">{Math.round(courseInfo.exams.reduce((s, e) => s + (e.gradeWeight || 0), 0) * 1000) / 10}%</span>
                          <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['exams'] ? '▼' : '▲'}</span>
                        </button>
                        {!gradingCollapsed['exams'] && courseInfo.exams.map((exam, idx) => (
                          <div key={idx} className="acd-weight-row">
                            <div className="acd-weight-info">
                              <span className="acd-weight-id">{exam.examId}</span>
                              <span className="acd-weight-title">{exam.title}</span>
                            </div>
                            {editingWeight?.type === 'exam' && editingWeight?.id === exam.examId ? (
                              <div className="acd-weight-edit">
                                <input type="number" min="0" max="100" step="0.1" autoFocus
                                  className="acd-weight-input" value={tempWeight}
                                  onChange={e => onTempWeightChange(e.target.value)} />
                                <span className="acd-weight-pct">%</span>
                                <button className="acd-wbtn acd-wbtn--save"   onClick={onWeightSave}>✓</button>
                                <button className="acd-wbtn acd-wbtn--cancel" onClick={onWeightCancel}>✕</button>
                              </div>
                            ) : (
                              <div className="acd-weight-display">
                                <span className="acd-weight-val">{Math.round((exam.gradeWeight || 0) * 1000) / 10}%</span>
                                <button className="acd-wbtn acd-wbtn--edit" onClick={() => onWeightEdit('exam', exam.examId, exam.gradeWeight)}>✏️</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {courseInfo?.capstones?.length > 0 && (
                      <div className="acd-grade-category">
                        <button className="acd-grade-category-hdr" onClick={() => toggleGrading('capstones')} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                          📘 Capstones
                          <span className="acd-grade-total">{Math.round(courseInfo.capstones.reduce((s, c) => s + (c.gradeWeight || 0), 0) * 1000) / 10}%</span>
                          <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['capstones'] ? '▼' : '▲'}</span>
                        </button>
                        {!gradingCollapsed['capstones'] && courseInfo.capstones.map((cap, idx) => (
                          <div key={idx} className="acd-weight-row">
                            <div className="acd-weight-info">
                              <span className="acd-weight-id">{cap.capstoneId}</span>
                              <span className="acd-weight-title">{cap.title}</span>
                            </div>
                            {editingWeight?.type === 'capstone' && editingWeight?.id === cap.capstoneId ? (
                              <div className="acd-weight-edit">
                                <input type="number" min="0" max="100" step="0.1" autoFocus
                                  className="acd-weight-input" value={tempWeight}
                                  onChange={e => onTempWeightChange(e.target.value)} />
                                <span className="acd-weight-pct">%</span>
                                <button className="acd-wbtn acd-wbtn--save"   onClick={onWeightSave}>✓</button>
                                <button className="acd-wbtn acd-wbtn--cancel" onClick={onWeightCancel}>✕</button>
                              </div>
                            ) : (
                              <div className="acd-weight-display">
                                <span className="acd-weight-val">{Math.round((cap.gradeWeight || 0) * 1000) / 10}%</span>
                                <button className="acd-wbtn acd-wbtn--edit" onClick={() => onWeightEdit('capstone', cap.capstoneId, cap.gradeWeight)}>✏️</button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Total */}
                    {(() => {
                      let total = 0;
                      courseInfo?.labs?.forEach(l => { total += (l.gradeWeight || 0) * 100; });
                      courseInfo?.quizzes?.forEach(q => { total += (q.gradeWeight || 0) * 100; });
                      courseInfo?.exams?.forEach(e => { total += (e.gradeWeight || 0) * 100; });
                      courseInfo?.capstones?.forEach(c => { total += (c.gradeWeight || 0) * 100; });
                      const rounded = Math.round(total * 10) / 10;
                      const valid = Math.abs(rounded - 100) < 0.5;
                      return (
                        <>
                          <div className={`acd-weight-summary ${valid ? 'acd-weight-summary--valid' : 'acd-weight-summary--invalid'}`}>
                            <span className="acd-weight-summary-label">Total Course Weight</span>
                            <span className="acd-weight-summary-value">{rounded}% {valid ? '✓' : '⚠'}</span>
                          </div>
                          {!valid && (
                            <div className="acd-weight-warning">⚠️ Warning: Total weight should equal 100%</div>
                          )}
                        </>
                      );
                    })()}
                  </div>

              </div>
              )}

              {/* Students Tab */}
              {activeTab === 'students' && (
                <div className="scrollable-list">
                  {students.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {students.map((student) => {
                        const loginId     = student.loginId || student.studentId;
                        const progress    = studentsProgress?.[loginId];
                        const cg          = student.courseGrades?.find(g => g.courseId === courseId);
                        const isPending   = cg?.letterGrade === 'P';
                        const passed      = !isPending && cg?.letterGrade !== 'F';
                        const isExpanded  = expandedStudents?.[loginId];

                        const labsList      = progress?.labProgress?.labs          || [];
                        const quizzesList   = progress?.quizProgress?.quizzes      || [];
                        const examsList     = progress?.examProgress?.exams        || [];
                        const capstonesList = progress?.capstoneProjects?.capstones || [];

                        const totalLabs      = courseInfo?.labs?.length      || 0;
                        const totalQuizzes   = courseInfo?.quizzes?.length   || 0;
                        const totalExams     = courseInfo?.exams?.length     || 0;
                        const totalCapstones = courseInfo?.capstones?.length || 0;

                        const gradedLabs      = labsList.filter(l      => l.attempts?.some(a => a.status === 'graded')).length;
                        const gradedQuizzes   = quizzesList.filter(q   => q.attempts?.some(a => a.status === 'graded')).length;
                        const gradedExams     = examsList.filter(e     => e.attempts?.some(a => a.status === 'graded')).length;
                        const gradedCapstones = capstonesList.filter(c => c.submissions?.some(s => s.status === 'graded')).length;

                        const badgeStyle = (pending, pass) => ({
                          padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 700,
                          background: pending ? 'var(--m-amber-dim)' : pass ? 'var(--m-green-dim)' : 'var(--m-red-dim)',
                          color:      pending ? 'var(--m-amber)'     : pass ? 'var(--m-green)'     : 'var(--m-red)',
                          border:     `1px solid ${pending ? 'var(--m-amber-border)' : pass ? 'var(--m-green-border)' : 'var(--m-red-border)'}`
                        });

                        const badgeLabel = !cg ? 'N/A' : isPending ? '⏳ Pending' : passed ? '✅ Pass' : '❌ Fail';

                        return (
                          <div key={loginId} className="acd-section" style={{ padding: '0', overflow: 'hidden' }}>

                            {/* ── Student Card Header ── */}
                            <div
                              style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', cursor: 'pointer', background: isExpanded ? 'var(--m-bg-elevated)' : 'var(--m-bg-surface)' }}
                              onClick={() => setExpandedStudents(prev => ({ ...prev, [loginId]: !prev[loginId] }))}
                            >
                              <img
                                src={student.imageUrl || '/images/default-avatar.png'}
                                alt={student.name}
                                style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--m-text-primary)' }}>{student.name}</div>
                                <div style={{ fontSize: '11px', color: 'var(--m-text-muted)' }}>{student.email}</div>
                                <div style={{ fontSize: '11px', color: 'var(--m-text-faint)' }}>{loginId}</div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: 700, fontSize: '18px', color: isPending ? 'var(--m-amber)' : passed ? 'var(--m-green)' : 'var(--m-red)' }}>
                                    {cg?.finalGrade?.toFixed(1) || '0.0'}%
                                  </div>
                                  <div style={{ fontSize: '11px', color: 'var(--m-text-muted)' }}>{getTimeAgo(student.updatedAt)}</div>
                                </div>
                                <span style={badgeStyle(isPending, passed)}>{badgeLabel}</span>
                                <span style={{ color: 'var(--m-text-muted)', fontSize: '12px' }}>{isExpanded ? '▲' : '▼'}</span>
                              </div>
                            </div>

                            {/* ── Expanded Detail ── */}
                            {isExpanded && (
                              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--m-border)', background: 'var(--m-bg-elevated)' }}>
                                <div className="submissions-table-container">
                                  <table className="submissions-table">
                                    <thead>
                                      <tr>
                                        <th>Category</th>
                                        <th className="center">Completed</th>
                                        <th className="center">Total</th>
                                        <th className="center">Average Grade</th>
                                        <th className="center">Weight</th>
                                        <th className="center">Weighted Score</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td><strong>🧪 Labs</strong></td>
                                        <td className="text-center"><span className="progress-fraction">{gradedLabs}</span></td>
                                        <td className="text-center"><span className="progress-fraction">{totalLabs}</span></td>
                                        <td className="text-center"><span className="grade-display">
                                          {gradedLabs > 0 ? ((labsList.reduce((s,l) => s + (l.bestGrade||0), 0) / labsList.reduce((s,l) => s + (courseInfo?.labs?.find(cl => cl.labId === l.labId)?.maxGrade || 1), 0)) * 100).toFixed(1) : '0.0'}%
                                        </span></td>
                                        <td className="text-center">
                                          <span className="grade-display">{(() => {
                                            const weight = labsList.reduce((s,l) => s + (courseInfo?.labs?.find(cl => cl.labId === l.labId)?.gradeWeight || 0), 0);
                                            return Math.round(weight * 1000) / 10;
                                            })()}%
                                          </span>
                                        </td>
                                        <td className="text-center"><span className="grade-display">{cg?.labsGrade?.toFixed(1) || '0.0'}%</span></td>
                                      </tr>
                                      <tr>
                                        <td><strong>📋 Quizzes</strong></td>
                                        <td className="text-center"><span className="progress-fraction">{gradedQuizzes}</span></td>
                                        <td className="text-center"><span className="progress-fraction">{totalQuizzes}</span></td>
                                        <td className="text-center"><span className="grade-display">
                                          {gradedQuizzes > 0 ? ((quizzesList.reduce((s,q) => s + (q.bestGrade||0), 0) / quizzesList.reduce((s,q) => s + (courseInfo?.quizzes?.find(cq => cq.quizId === q.quizId)?.maxGrade || 1), 0)) * 100).toFixed(1) : '0.0'}%
                                        </span></td>
                                        <td className="text-center">
                                          <span className="grade-display">{(() => {
                                            const weight = quizzesList.reduce((s,q) => s + (courseInfo?.quizzes?.find(cq => cq.quizId === q.quizId)?.gradeWeight || 0), 0);
                                            return Math.round(weight * 1000) / 10;
                                          })()}%
                                          </span>
                                        </td>
                                        <td className="text-center"><span className="grade-display">{cg?.quizzesGrade?.toFixed(1) || '0.0'}%</span></td>
                                      </tr>
                                      <tr>
                                        <td><strong>📄 Exams</strong></td>
                                        <td className="text-center"><span className="progress-fraction">{gradedExams}</span></td>
                                        <td className="text-center"><span className="progress-fraction">{totalExams}</span></td>
                                        <td className="text-center"><span className="grade-display">
                                          {gradedExams > 0 ? ((examsList.reduce((s,e) => s + (e.finalGrade||0), 0) / examsList.reduce((s,e) => s + (courseInfo?.exams?.find(ce => ce.examId === e.examId)?.maxGrade || 1), 0)) * 100).toFixed(1) : '0.0'}%
                                        </span></td>
                                        <td className="text-center">
                                          <span className="grade-display">{(() => {
                                            const weight = examsList.reduce((s,e) => s + (courseInfo?.exams?.find(ce => ce.examId === e.examId)?.gradeWeight || 0), 0);
                                            return Math.round(weight * 1000) / 10;
                                          })()}%
                                          </span>
                                        </td>
                                        <td className="text-center"><span className="grade-display">{cg?.examsGrade?.toFixed(1) || '0.0'}%</span></td>
                                      </tr>
                                      <tr>
                                        <td><strong>📘 Capstones</strong></td>
                                        <td className="text-center"><span className="progress-fraction">{gradedCapstones}</span></td>
                                        <td className="text-center"><span className="progress-fraction">{totalCapstones}</span></td>
                                        <td className="text-center"><span className="grade-display">
                                          {gradedCapstones > 0 ? ((capstonesList.reduce((s,c) => s + (c.bestGrade||0), 0) / capstonesList.reduce((s,c) => s + (courseInfo?.capstones?.find(cc => cc.capstoneId === c.capstoneId)?.maxGrade || 1), 0)) * 100).toFixed(1) : '0.0'}%
                                        </span></td>
                                        <td className="text-center">
                                          <span className="grade-display">{(() => {
                                            const weight = capstonesList.reduce((s,c) => s + (courseInfo?.capstones?.find(cc => cc.capstoneId === c.capstoneId)?.gradeWeight || 0), 0);
                                            return Math.round(weight * 1000) / 10;
                                          })()}%
                                          </span>
                                        </td>
                                        <td className="text-center"><span className="grade-display">{cg?.capstoneGrade?.toFixed(1) || '0.0'}%</span></td>
                                      </tr>
                                      <tr style={{ background: 'var(--m-bg-card)', fontWeight: 'bold' }}>
                                        <td><strong>📊 Overall</strong></td>
                                        <td className="text-center"><span className="progress-fraction">{gradedLabs + gradedQuizzes + gradedExams + gradedCapstones}</span></td>
                                        <td className="text-center"><span className="progress-fraction">{totalLabs + totalQuizzes + totalExams + totalCapstones}</span></td>
                                        <td className="text-center"><span className="grade-display" style={{ color: 'var(--m-text-muted)', fontSize: '12px' }}>—</span></td>
                                        <td className="text-center">
                                          <span className="grade-display">
                                          {(() => {
                                            let totalWeight = 0;
                                            labsList.forEach(l => { totalWeight += (courseInfo?.labs?.find(cl => cl.labId === l.labId)?.gradeWeight || 0); });
                                            quizzesList.forEach(q => { totalWeight += (courseInfo?.quizzes?.find(cq => cq.quizId === q.quizId)?.gradeWeight || 0); });
                                            examsList.forEach(e => { totalWeight += (courseInfo?.exams?.find(ce => ce.examId === e.examId)?.gradeWeight || 0); });
                                            capstonesList.forEach(c => { totalWeight += (courseInfo?.capstones?.find(cc => cc.capstoneId === c.capstoneId)?.gradeWeight || 0); });
                                            return Math.round(totalWeight * 1000) / 10;
                                          })()}%
                                          </span>
                                        </td>
                                        <td className="text-center">
                                          <span className="grade-display" style={{ color: isPending ? 'var(--m-amber)' : passed ? 'var(--m-green)' : 'var(--m-red)' }}>
                                            {cg?.finalGrade?.toFixed(1) || '0.0'}%
                                            <span style={{ ...badgeStyle(isPending, passed), marginLeft: '6px' }}>
                                              {isPending ? '⏳ Pending' : passed ? '✅ Pass' : '❌ Fail'}
                                            </span>
                                          </span>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-items">
                      <p>No students enrolled yet 👨‍🎓</p>
                    </div>
                  )}
                </div>
              )}

              {/* Grading Tab */}
              {activeTab === 'grading' && (
                <div className="scrollable-list">

                  {/* Statistics Cards */}
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-icon">👨‍🎓</div>
                        <div className="stat-info">
                          <h3>{statsAssignments.allPending} | {statsAssignments.allSubmitted}</h3>
                          <p>Pending/Submitted Submitions</p>
                        </div>
                    </div>
                  </div>

                  <div className="bulk-grading-header">
                    {Object.keys(gradingChanges).length > 0 && (
                      <CustomButton 
                        buttonTitle={`✅ Submit All Grades (${Object.keys(gradingChanges).length} changes)`}
                        action={handleGradeAll}
                        bgColor="#10b981"
                      />
                    )}
                  </div>

                  {renderGradingSection(labGroups, 'lab', expandedLabs, '🧪', 'Lab Reports')}
                  {renderGradingSection(quizGroups, 'quiz', expandedQuizzes, '📋', 'Quizzes')}
                  {renderGradingSection(examGroups, 'exam', expandedExams, '📄', 'Exams')}
                  {renderGradingSection(capstoneGroups, 'capstone', expandedCapstones, '📘', 'Capstone Projects')}
                </div>
              )}

              {/* Messages Tab — ConversationModal handles both messages AND announcements internally */}
              {activeTab === 'communication' && (
                <ConversationModal
                  role="instructor"
                  messages={conversationMessages}
                  students={students}
                  selectedStudent={selectedStudent}
                  onSelectStudent={handleSelectStudent}
                  studentsWithUnread={studentsWithUnread}
                  currentUserId={authData?.loginId || authData?.id}
                  messageText={messageText}
                  onMessageTextChange={setMessageText}
                  onSendMessage={handleSendMessage}
                  announcementTitle={announcementTitle}
                  onAnnouncementTitleChange={setAnnouncementTitle}
                  announcementBody={announcementBody}
                  onAnnouncementBodyChange={setAnnouncementBody}
                  announcementPriority={announcementPriority}
                  onAnnouncementPriorityChange={setAnnouncementPriority}
                  announcementHistory={announcementHistory}
                  onSendAnnouncement={handleSendCourseAnnouncement}
                  sendingAnnouncement={sendingAnnouncement}
                  onViewAnnouncement={(ann) => { setSelectedAnnouncement(ann); setAnnouncementModalVisible(true); }}
                />
              )}

              {/* AnnouncementDetailModal — always mounted outside tabs */}
              {announcementModalVisible && selectedAnnouncement && (
                <AnnouncementDetailModal
                  visible={announcementModalVisible}
                  onClose={() => setAnnouncementModalVisible(false)}
                  announcement={selectedAnnouncement}
                />
              )}

              <ProfIXChatbot 
                userName={authData.name}
                courseId={courseId} 
                courseName={courseInfo?.title} 
                category={courseInfo?.category}
              />

            </>
          )}
        </div>
      </div>

      {/* ====================================================================== */}
      {/* INSTRUCTOR COURSE CONTENT MODALS */}
      {/* ====================================================================== */}
      {/* Instructor List Modal */}
      <ContentListModal
        visible={showListModal && !!selectedListType}
        onClose={closeListModal}
        role="instructor"
        contentType={selectedListType}
        items={courseInfo?.[selectedListType] || []}
        onView={(itemId) => openContentModalFromList(selectedListType, itemId)}
        onAdd={() => handleAddNewItem(selectedListType)}
        onDelete={(itemId) => handleDeleteItem(selectedListType, itemId)}
      />

      {/* Instructor LESSON CONTENT MODAL */}
      <LessonModal
        visible={showContentModal && selectedContentType === 'lessons'}
        onClose={closeContentModal}
        role="instructor"
        lesson={(() => {
          return courseInfo?.lessons?.find(l => l.lessonId === selectedItemId) || null;
        })()}
        authData={authData}
        courseId={courseId}
        onLoad={loadPage}
        onDownload={handleDownloadAttachment}
        onStageFiles={handleStageFiles}
        onUploadFiles={uploadContentFiles}
        onRemoveNewFile={removeContentFile}
        pendingFiles={editedContentFiles}
        isUploadingFiles={uploadingContentFiles}
        uploadProgress={contentFileUploadProgress}
        onAfterSave={() => setEditedContentFiles({})}
      />
      
      {/* ASSESSMENT CONTENT View/Edit MODAL — labs / quizzes / exams */}
      <AssessmentModal
        visible={showContentModal && (
          selectedContentType === 'labs' ||
          selectedContentType === 'quizzes' ||
          selectedContentType === 'exams'
        )}
        authData={authData}
        courseId={courseId}
        domain={domainName}
        role="instructor"
        phase="overview"
        assessmentType={
          selectedContentType === 'labs' ? 'lab' :
          selectedContentType === 'quizzes' ? 'quiz' : 'exam'
        }
        content={(() => {
          if (!selectedContentType || !selectedItemId) return null;
          const items = isEditMode ? editedContent : courseInfo?.[selectedContentType];
          const idField = selectedContentType === 'labs' ? 'labId' :
          selectedContentType === 'quizzes' ? 'quizId' : 'examId';
          return items?.find(i => i[idField] === selectedItemId) || null;
        })()}
        onLoad={loadPage}
        onClose={closeContentModal}
        onDownload={handleDownloadAttachment}
        onUploadFiles={uploadContentFiles}
        onRemoveNewFile={removeContentFile}
      />

      {/* ASSESSMENT CONTENT GRADING MODAL — lab / quiz / exam */}
      <AssessmentModal
        visible={showSubmissionModal &&
                selectedSubmission?.type !== 'capstone'}
        authData={authData}
        courseId={courseId}
        domain={domainName}
        role="instructor"
        phase="grading"
        assessmentType={selectedSubmission?.type || 'lab'}
        content={selectedSubmission}
        onClose={closeSubmissionModal}
        onDownload={handleDownloadAttachment}
        onUploadFiles={uploadContentFiles}
        onRemoveNewFile={removeContentFile}
        // LabAnalysis={SimulationReport}
        submission={selectedSubmission}
        gradeFormData={gradeFormData}
        onGradeFormChange={setGradeFormData}
        labSessions={selectedSubmission?.labSessions || {}}
        labGradingModals={labGradingModals}
        setLabGradingModals={setLabGradingModals}
        onSubmitGrade={handleSubmitSingleGrade}
        allStudentCircuits={allStudentCircuits}
      />

    </div>
  );
};

export default InstructorCourseManagement;